import { LitElement, html, css, nothing, keyed } from "./lit-all.min.js";

const handlise = (string) => {
	return string.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").replace(/[^a-z0-9]+/g, '-');
}

// Force refresh cache every hour
const fileKey = Math.round(Date.now() / 1000 / 60 / 60);

const getSwatchUrl = (colour) => 
	`${window.fileUrlPrefix}swatch-${handlise(colour)}.jpg?width=80&v=${fileKey}`;

const getOptionName = (name) => {
	if (name == 'Colour' && theme.countryCode == 'US') return 'Color';
	if (name == 'Fabric') return theme.countryCode == 'US' ? 'Fiber' : 'Fibre';
	else return name;
}

let swatchesHexRequest = null;

const onSwatchError = async (e) => {
	if (!swatchesHexRequest) swatchesHexRequest = fetch('/?view=bundle-colours').then(resp => resp.json());

	const colours = await swatchesHexRequest;
	const colourId = e.target.src.match(/swatch-([a-z0-9-]+)\.jpg/)[1];
	const hex = colours.find(c => c.id == colourId)?.hex;

	e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAAA1BMVEUAAACnej3aAAAAAXRSTlMAQObYZgAAAApJREFUCNdjYAAAAAIAAeIhvDMAAAAASUVORK5CYII=';
	e.target.style.backgroundColor = hex ? hex : '#F0F0F0';
}

const fireEvent = (action, detail, feature) => {
	if (window.trackEvent) window.trackEvent('BYOB', action, detail, feature);
}

const trackEvent = (name, properties = {}) => {
	if (window.FS) {
		window.FS('trackEvent', {
			name: name,
			properties: properties
		});

		if (window.location.hostname.includes('127.0.0.1')) {
			console.log('Track FullStory event', name, properties);
		}
	}
}

const buttonStyles = css`
	.button {
		padding: 8px 16px;
		border-radius: 2px;
		font: var(--text-md);
		text-decoration: none;
		text-align: center;
		display: inline-flex;
		gap: 8px;
		justify-content: center;
		align-items: center;
		outline: none;
		-webkit-tap-highlight-color: transparent;
		background: none;
		border: none;
		cursor: pointer;
		white-space: nowrap;
	}

	/* Icon */
	.button::before {
		display: block;
		width: 24px;
		height: 24px;
		background-size: 100%;
		background-repeat: no-repeat;
	}

	@media screen and (min-width: 768px) {
		.button {
			font: var(--text-lg);
		}
	}

	.button[disabled] {
		cursor: not-allowed;
	}

	.button--primary {
		background-color: var(--color-button-bg-primary);
		color: var(--color-button-text-primary) !important;
	}

	.button--primary.button--inverted {
		background-color: var(--color-button-bg-secondary);
		color: var(--color-text-primary) !important;
	}

	.button--secondary {
		background-color: var(--color-button-bg-secondary);
		border: 1px solid var(--color-border-button-primary);
		color: var(--color-text-primary) !important;
	}

	.button--secondary.button--inverted {
		background: none;
		color: var(--color-text-inverted-primary, #FFF) !important;
		border: 1px solid var(--color-border-icon-inverted-primary, #FFF);
	}

	.button--tertiary {
		border: none;
		text-decoration: underline;
		text-decoration-color: var(--color-button-text-tertiary);
		padding: var(--padding-xs) 0px;
		color: var(--color-button-text-tertiary)
	}

	.button--tertiary.button--inverted {
		color: var(--color-text-inverted-primary, #FFF) !important;
		text-decoration-color: var(--color-text-inverted-primary, #FFF) !important;
	}

	.button--link {
		border: none;
		background: none;
	}

	.enable-design-system .button--secondary {
		border: 1px solid var(--color-border-button-primary, #BDBDBD);
	}

	@media screen and (hover: hover) {
		.button--primary:hover,
		.button--secondary:hover,
		.button--tertiary:hover,
		.button--link:hover {
			opacity: 0.8;
		}
	}

	.button--primary:active {
		background-color: #292929;
	}

	.button--secondary:active,
	.button--tertiary:active {
		background-color: var(--button-bg-active);
	}

	.button--link:active {
		opacity: 0.5;
	}

	.button--primary[disabled] {
		opacity: 0.5;
	}

	.button--secondary[disabled] {
		background: var(--color-surface-surface-tertiary, #EBEBEB); 
		border: 1px solid var(--color-surface-surface-tertiary, #EBEBEB);
		color: var(--color-text-text-disabled, #949494) !important; 
	}

	.button--error {
		color: red;
		border: 1px solid red;
	}
`;

class BundleStartButton extends LitElement {
	static get properties() {
		return {
			ready: { type: Boolean },
		}
	}

	static started = false;

	constructor() {
		super();
		this.ready = false;
	}

	connectedCallback() {
		this.classList.add('init');
		
		const params = new URLSearchParams(window.location.search);
		
		const autoloadType = 
			params.has('wishlist') ? 'Wishlist' 
			: params.has('preset') ? 'Customise Preset' 
			: params.has('config') ? 'URL Config' 
			: params.has('hl') ? 'AR Return to Product' 
			: params.has('edit') ? 'Edit Bundle'
			: params.get('start') == 'true' ? 'Autoload'
			: null;
		this.autoLoad = autoloadType != null;
		this.hasSession = sessionStorage.getItem(`bundleBuilderState${theme.product.id}`) != null;

		if (this.autoLoad) {
			if (window.bundleBuilderSessionReady) this.showBundleBuilder(false, autoloadType);
			window.addEventListener('bundlebuildersessionready', () => this.showBundleBuilder(false, autoloadType));
		} else {
			this.classList.remove('show-loading', 'show-loading--inverted');
			if (window.bundleBuilderReady) this.ready = true;
			window.addEventListener('bundlebuilderready', () => this.ready = true);
		}

		window.onFeatureEnabled('byob-onboarding', () => {
			if (window.bundleBuilderSessionReady) this.showBundleBuilder(false, 'Onboarding');
			window.addEventListener('bundlebuildersessionready', () => this.showBundleBuilder(false, 'Onboarding'));
		});

		super.connectedCallback();
	}

	showBundleBuilder(restart = false, type = null) {
		if (BundleStartButton.started) return;
		BundleStartButton.started = true;

		if (restart) document.querySelector('bundle-builder').restart();
		document.body.classList.add('bundle-transition');

		if (!this.hasSession && (!this.autoLoad || type == 'Autoload')) {
			document.querySelector('bundle-onboarding')?.open();
		}
		else if (type == 'Customise Preset') {
			document.querySelector('bundle-onboarding').open(false, true);
		}
		else if (type == 'Onboarding' && this.hasSession && !this.autoLoad) {
			document.querySelector('bundle-onboarding')?.open(true);
		}

		setTimeout(() => {
			document.body.classList.remove('bundle-transition');
			document.body.classList.add('bundle-started');

			if (typeof window.startTableRender === 'function') window.startTableRender();
		}, 150);

		if (window.innerWidth > 992) window.scrollTo({top: 0, behaviour: 'auto'});

		trackEvent('BYOB Started', { type: type ?? 'None', startTime: performance.now() });
	}

	render() {
		return html`
			${this.hasSession && !this.autoLoad ? html`
				<button class="primary resume ${!this.ready ? 'loading loading--inverted': ''}" @click=${() => this.showBundleBuilder(false, 'Keep Editing Bundle')}>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M15.0534 3.69863L12.4445 1.07641C12.2721 0.904884 12.0388 0.808594 11.7956 0.808594C11.5524 0.808594 11.3191 0.904884 11.1467 1.07641L1.8978 10.312L1.05336 13.9564C1.02423 14.0896 1.02523 14.2277 1.05628 14.3605C1.08734 14.4933 1.14766 14.6174 1.23285 14.7239C1.31804 14.8304 1.42595 14.9165 1.54868 14.976C1.67141 15.0354 1.80588 15.0667 1.94225 15.0675C2.00579 15.0739 2.06982 15.0739 2.13336 15.0675L5.8178 14.2231L15.0534 4.99641C15.2249 4.82401 15.3212 4.59071 15.3212 4.34752C15.3212 4.10433 15.2249 3.87103 15.0534 3.69863ZM5.37336 13.4231L1.92003 14.1475L2.70669 10.7609L9.62669 3.86752L12.2934 6.53419L5.37336 13.4231ZM12.8889 5.88974L10.2222 3.22308L11.7689 1.6853L14.3911 4.35197L12.8889 5.88974Z" fill="white"/>
					</svg>
					Keep editing your Bundle
				</button>
				<button class="secondary restart ${!this.ready ? 'loading': ''}" @click=${() => this.showBundleBuilder(true, 'Start New Bundle')}>
					Start a New Bundle
				</button>
			` : html`
				<button class="primary ${!this.ready ? 'loading loading--inverted': ''}" @click=${() => this.showBundleBuilder(false)}>
					<slot></slot>
				</button>
			`}
		`
	}

	static get styles() {
		return css`
			:host {
				display: flex;
				flex-direction: column;
				gap: var(--distance-d-2, 8px);
			}

			.primary, .secondary {
				height: var(--height);
				border-radius: var(--button-border-radius);
				font: var(--text-md);
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				gap: 10px;
				width: 100%;
				white-space: nowrap;
				padding: var(--padding-xs) var(--padding-sm);
			}

			.primary {
				background-color: var(--color-button-bg-primary);
				color: var(--color-button-text-primary);
				border: none;
			}

			@media (hover: hover) {
				.primary:hover {
					background-color: #3d3d3d;
				}
			}

			.primary:active {
				background-color: #292929;
			}
		
			.secondary {
				border: 1px solid var(--color-border-border-primary, #E1E1E1);
				background: white;
				color: var(--color-text-primary);
			}

			@media (hover: hover) {
				.secondary:hover {
					background-color: var(--button-bg-hover);
				}
			}

			.secondary:active {
				background-color: var(--button-bg-active);
			}

			:host(.show-loading) .primary {
				color: transparent;
				opacity: 0.6;
			}

			.primary.loading {
				opacity: 0.6;
			}

			.loading {
				position: relative;
				color: transparent !important;
				pointer-events: none;
			}

			.loading svg {
				opacity: 0;
			}
			
			.loading::after {
				content: "";
				position: absolute;
				height: 16px;
				width: 16px;
				left: calc(50% - 8px);
				top: calc(50% - 8px);
				opacity: 60%;
				animation: show-loading-anim 1s linear infinite;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z'/%3E%3C/svg%3E");
			}
			
			.loading--inverted::after {
				filter: invert(1);
			}

			@keyframes show-loading-anim {
				0% {
					transform: rotate(0deg);
				}
				100% {
					transform: rotate(360deg);
				}
			}


			:host([location="sticky"]) .resume, :host([location="sticky"]) .restart {
				height: 38px;
			}

			@media screen and (min-width: 992px) {
				:host([location="sticky"]) {
					flex-direction: row;
					flex-basis: unset !important;
				}

				:host([location="sticky"]) .primary, :host([location="sticky"]) .secondary {
					width: 270px;
				}

				:host([location="sticky"]) .resume, :host([location="sticky"]) .restart {
					height: 44px;
				}

				.primary, .secondary {
					font: var(--text-lg);
				}
			}
		`;
	}
}

customElements.define('bundle-start-button', BundleStartButton);

/**
 * @typedef {Object} Config
 * @property {Selector[]} tabs
 * @property {Selector[]} extras
 * @property {Number} discount
 * @property {string[]} hiddenColours
 * @property {string[]} extrasText
 */

/**
 * @typedef {Object} Selector
 * @property {string} id
 * @property {string} name
 * @property {string[]} collections
 * @property {string[]} types
 * @property {Number} discountOverride
 */


class BundleBuilder extends LitElement {
	static get properties() {
		return {
			productTitle: { type: String },
			currentTab: { type: String },
			currentStep: { type: String, reflect: true },
			viewedTabs: { type: Array },
			currencySymbol: { type: String },
			currency: { type: String },
			parentId: { type: String },
			parentSku: { type: String },
			selectedOptions: { type: Object },
			preOrderMonth: { type: String },
			sizeGuide: { type: String },
			tooltip: { type: String },
			bundleImage: { type: String },
			hideExtras: { type: Boolean },
			tieredDiscount: { type: Boolean, reflect: true },
			fullscreen: { type: Boolean, reflect: true },
			modalOpen: { type: Boolean },
			lineItem: { type: String },
		}
	}

	constructor() {
		super();

		/** @type {Config} */
		this.config = {};

		this.currencySymbol = '';
		this.currency = '';
		
		this.sessionId = Date.now();
		this.lineItem = '';
		this.currentTab = null;
		this.currentStep = null;
		this.viewedTabs = [];
		this.selectedOptions = {};

		this.selectedVariants = new Map();
		this.autoSelectOptions = {};
		this.flipped = {};

		this.loadedSelectors = new Set();
		this.ready = false;
		this.sessionReady = false;
		this.loaded = false;
		this.stateLoading = false;
		this.parentId = '';
		this.parentSku = '';

		this.presetSource = null;
		this.presetItems = null;
		this.hideExtras = false;
		this.tieredDiscount = theme.b2b !== true && this.getAttribute('parentsku') == 'BT-BYOBUNDLE';
		this.tiers = [0, 5, 10, 15, 20];

		this.sizeGuide = null;
		this.bundleImage = null;

		this.fullscreen = false;
		this.modalOpen = false;

		this.addEventListener('tabchange', e => this.currentTab = e.detail);
		this.addEventListener('variantremove', e => this.removeVariant(e.detail));
		this.addEventListener('variantadd', e => this.selectVariant(e.detail.id, e.detail.variant));
		this.addEventListener('restart', () => this.restart());

		window.addEventListener('onboardingcomplete', e => {
			if (e.detail.config) {
				this.loadPresetConfig(e.detail.config);
				this.presetSource = 'modal';
			}

			if (e.detail.size) {
				this.autoSelectOptions = { 'Size': e.detail.size };
				this.requestUpdate();
			}

			if (e.detail.fabric) {
				this.autoSelectOptions['Fabric'] = e.detail.fabric;
				this.requestUpdate();
			}
		});

		this.addEventListener('opencolourinfo', e => {
			const modal = document.getElementById(`colour-info-${e.detail}`);
			if (modal) {
				modal.open();
				trackEvent('BYOB Colour Details Modal Opened', { colour: e.detail });
			}
		});

		this.addEventListener('modalopen', () => this.modalOpen = true);
		this.addEventListener('modalstartclose', () => this.modalOpen = false);

		window.addEventListener('renderupdate', e => {
			if (Object.keys(this.selectedOptions).length >= this.config.tabs.length || this.currentTab == 'Summary') {
				for (const button of this.querySelectorAllSlot('wishlist-add-button-byob')) {
					button.dataset.image = e.detail.thumb;
				}

				const bundleImages = this.querySelectorSlot('bundle-images');
				if (bundleImages) bundleImages.setAttribute('screenshot', e.detail.image);

				const signifierImage = document.querySelector('.cart-signifier__image img');
				if (signifierImage) signifierImage.src = e.detail.thumb;

				this.bundleImage = e.detail.thumb;
			}

			const onboarding = document.querySelector('bundle-onboarding');
			if (onboarding) onboarding.setAttribute('sessionImage', e.detail.image);
			sessionStorage.setItem(`bundleImage${theme.product.id}`, e.detail.image);

		});

		const observer = new IntersectionObserver(async entries => {
			if (entries.some(e => e.isIntersecting)) {
				observer.disconnect();

				// Load colour info
				const resp = await fetch('/?view=colour-info');
				const html = await resp.text();
				const container = document.createElement('div');
				container.innerHTML = html;
				this.appendChild(container);
			}
		});

		observer.observe(this);
		
		window.onFeatureEnabled('byob-hide-extras', () => this.hideExtras = true);
		window.onFeatureEnabled('disable-tiered-discount', () => this.tieredDiscount = false);

		this.bundleValid = false;

		window.bundleBuilder = this;
	}

	connectedCallback() {
		this.config = JSON.parse(this.getAttribute('config'));
		this.config.tabs.forEach(tab => tab.types = tab.types.map(type => type.replace(/(Scalloped|Embroidered)/, '').trim()));

		// Load state
		this.stateId = `bundleBuilderState${theme.product.id}`;
		this.loadState();

		super.connectedCallback();
	}

	getMaxPrice(size = null, priceTest = false) {
		const variantSelectors = this.shadowRoot.querySelectorAll('bundle-variant-selector:not([extra])');
		let totalMaxPrice = 0;
		let maxPriceSkus = [];

		for (const selector of variantSelectors) {
			let maxPrice = null;
			let maxPriceSku = null;

			let maxPriceSize = null;
			let maxPriceSizeSku = null;

			for (const variant of selector.variants) {
				const prices = [parseFloat(variant.price.amount), ...(variant.priceTests && priceTest ? variant.priceTests : [])];
				const price = Math.min(...prices);

				if (maxPrice == null || price > maxPrice) {
					maxPrice = price;
					maxPriceSku = variant.product.originalType;
				}

				let originalVariantSize = variant.selectedOptions.find(option => option.name == 'Size' && !variant.product.originalType.includes('Pillowcase'))?.value;
				let variantSize = originalVariantSize;
				if (variantSize == 'Medium') variantSize = 'Queen/King'
				else if (variantSize == 'Large') variantSize = theme.storeLocation == 'US' ? 'King/Cali King' : 'King/Super King';

				if (size && variantSize && (variantSize == size || (variantSize.includes('/') && variantSize.includes(size)))) {
					if (maxPriceSize == null || price > maxPriceSize) {
						maxPriceSize = price;
						maxPriceSizeSku = `${originalVariantSize} ${variant.product.originalType}`;
					}
				}
			}

			if (maxPriceSize) {
				maxPrice = maxPriceSize;
				maxPriceSku = maxPriceSizeSku;
			}

			totalMaxPrice += maxPrice;
			maxPriceSkus.push(`${maxPriceSku}`);
		}

		return { originalPrice: totalMaxPrice, maxPrice: totalMaxPrice * (1 - this.config.discount), skus: maxPriceSkus };
	}

	getSalePrices(saleMultiplier = 0.8, priceTest = false) {
		let sizes = [];

		if (theme.storeLocation == 'AU') sizes = ['Single', 'King Single', 'Double', 'Queen', 'King', 'Super King'];
		else if (theme.storeLocation == 'US') sizes = ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cali King'];
		else if (theme.storeLocation == 'UK') sizes = ['Single', 'Double', 'King', 'Super King'];

		let rows = [['Size', 'Original', 'Discounted', 'Sale', 'Total discount', 'SKUs']]

		for (const size of sizes) {
			const { originalPrice, maxPrice, skus } = this.getMaxPrice(size, priceTest);
			const salePrice = maxPrice * saleMultiplier;
			rows.push([size, originalPrice, maxPrice, salePrice.toFixed(1), (originalPrice - salePrice).toFixed(1), skus.join('; ')]);
		}

		const table = document.createElement('table');
		table.innerHTML = rows.map((row, idx) => `<tr>${row.map(cell => idx == 0 ? `<th>${cell}</th>` : `<td>${cell}</td>`).join('')}</tr>`).join('');

		// Copy to clipboard
		navigator.clipboard.write([new ClipboardItem({
			'text/html': new Blob([table.outerHTML], { type: 'text/html' })
		})]);

		console.log(rows.map(row => row.join(',')).join('\n'));
	}

	getSelectors() {
		return this.config.tabs.concat(this.config.extras);
	}

	getSelectedVariantsArray() {
		return [
			...this.config.tabs.map(tab => this.selectedVariants.get(tab.id)), 
			...this.config.extras.map(extra => this.selectedVariants.get(extra.id))].filter(v => v);
	}

	getSelectedItemsArray() {
		return [
			...this.config.tabs.map(t => this.selectedOptions[t.id]).filter(o => o).map(o => ({...o, Extra: false})),
			...this.config.extras.map(t => this.selectedOptions[t.id]).filter(o => o).map(o => ({...o, Extra: true}))
		];
	}

	getDiscount() {
		if (this.tieredDiscount) {
			const selectedCount = this.getSelectedVariantsArray().length;
			if (selectedCount == 0) return 0;
			if (selectedCount > this.tiers.length) return this.tiers[this.tiers.length - 1] / 100;
			return this.tiers[selectedCount - 1] / 100;
		}
		else return this.config.discount;
	}

	requiredComplete() {
		const minSteps = this.tieredDiscount ? 2 : this.config.tabs.length;
		const completed = this.config.tabs.filter(tab => this.selectedVariants.has(tab.id));
		const firstIncomplete = this.config.tabs.findIndex(tab => !this.selectedVariants.has(tab.id));
		const completedAfterIncomplete = this.config.tabs.find((tab, idx) => firstIncomplete != -1 && this.selectedVariants.has(tab.id) && idx > firstIncomplete);

		// Confirm that we've completed the minimum amount of steps, and no incomplete steps are before a completed one
		return completed.length >= minSteps && !completedAfterIncomplete;
	}

	extrasComplete() {
		if (!this.config.extras) return true;
		return this.config.extras.every(extra => !this.selectedOptions[extra.id] || this.selectedVariants.has(extra.id));
	}

	nextTab(e) {
		const current = this.currentTab;

		if (e.target.ariaDisabled == 'true') {
			fireEvent('Inactive-Button', e.target.innerText.trim(), this.currentTab);
			return;
		}
		if (this.currentTab == 'Summary') return;
		else if (this.currentTab == 'Extras') this.currentTab = 'Summary';
		else {
			const currentIndex = this.config.tabs.findIndex(t => t.name == this.currentTab);
			if (currentIndex < this.config.tabs.length - 1) this.currentTab = this.config.tabs[currentIndex + 1].name;
			else this.currentTab = this.config.extras && this.config.extras.length > 0 && !this.hideExtras ? 'Extras' : 'Summary';
		}

		trackEvent('BYOB Next Step', { current: current, next: this.currentTab });
		fireEvent(this.currentTab == 'Summary' ? 'Go-To-Summary' : 'Next-Step', null, this.currentTab);
	}

	selectVariant(id, variant) {
		if (variant == null) this.selectedVariants.delete(id);
		else {
			this.selectedVariants.set(id, variant);
			// Add options present on variant that might not have been explicitly selected as there was only one option value, eg Type
			// This allows the variant to be selected correctly when reloading state if another product type was added
			this.selectedOptions[id] = {...variant.options, ...this.selectedOptions[id]}; 
		}

		this.requestUpdate();
		this.updateRender();

		const bundleImages = this.querySelectorSlot('bundle-images');

		if (bundleImages) {
			const variants = this.getSelectedVariantsArray();
			bundleImages.setAttribute('images', JSON.stringify(variants.map(v => v.product.featuredImage.url)));
			bundleImages.setAttribute('captions', JSON.stringify(variants.map(v => v.product.productType)));
		}
	}

	removeVariant(id) {
		const extra = this.config.extras.find(extra => extra.id == id);
		if (extra) this.setExtraActive(extra, false);
		else delete this.selectedOptions[id];

		const productName = this.config.tabs.find(tab => tab.id == id)?.name || this.config.extras.find(extra => extra.id == id)?.name;
		for (const key of [...BundleOptionSelector.previousSelections.keys()]) {
			if (key.startsWith(productName)) BundleOptionSelector.previousSelections.delete(key);
		}

		this.requestUpdate();
	}

	selectOptions(id, options) {
		// If no variants selected yet (ie, first step), auto-select Size and Fabric for subsequent steps
		if (this.config.tabs.findIndex(tab => tab.id == id) == this.selectedVariants.size) {
			if (options['Size'] && !options['Size'].includes('/')) {
				this.autoSelectOptions['Size'] = options['Size'];
			}	

			if (options['Fabric'] && this.config.tabs.findIndex(tab => tab.id == id) == this.selectedVariants.size) {
				this.autoSelectOptions['Fabric'] = options['Fabric'];
			} 
		}
		
		// Option change events
		const changes = Object.entries(options).filter(([key, value]) => 
			!this.selectedOptions[id] || this.selectedOptions[id][key] != value
		);
		const extra = this.config.extras.find(extra => extra.id == id);
		const extraName = extra ? extra.name.split('(')[0].trim() : null;
		
		for (const [name, value] of changes) {
			if (name == 'Size') {
				fireEvent('Size-Select', `Size-Select-${extraName ? extraName + '-' : ''}${value}`, this.currentTab);
			}
			else if (name == 'Colour') {
				fireEvent('Colour-Select', `Colour-Select-${extraName ? extraName + '-' : ''}${options['Type']}-${value}`, this.currentTab);
			}
		}

		this.selectedOptions[id] = options;
		this.sessionId = Date.now(); // Create new session ID for wishlist
		this.updateRender();
		this.saveState();
	}

	setExtraActive(extra, value) {
		if (value == true) this.selectedOptions[extra.id] = {};
		else delete this.selectedOptions[extra.id];
		this.sessionId = Date.now(); // Create new session ID for wishlist
		this.requestUpdate();
		this.updateRender();
	} 

	onAddToCart() {
		const hasPresetItems = this.presetItems && this.presetItems.length > 0;
		const matchedPresetItems = new Set();
		const finalItems = this.getSelectedItemsArray();
		finalItems.forEach(item => item.Match = null);

		for (const item of finalItems) {
			if (hasPresetItems) {
				const match = this.presetItems.find(preset => 
					// Check if there is a preset item where type, colour, and extra match
					!matchedPresetItems.has(preset) && Object.keys(preset).every(key => preset[key] == item[key])
				);
				
				if (match) {
					item.Match = true;
					matchedPresetItems.add(match);
				}
				else item.Match = false;
			}
			else finalItems.Match = null; 
		}

		if (hasPresetItems) fireEvent('ATC', 'ATC-BYOB-Curated', finalItems);
		else fireEvent('ATC', 'ATC-BYOB-Scratch', finalItems);
	}
	
	setFlip(id, value) {
		this.flipped[id] = value;
		trackEvent('BYOB Flip my Quilt Clicked', { flipped: value });
		this.requestUpdate();
		this.updateRender();
	}

	getState() {
		const selectors = this.getSelectors();
		const items = selectors.map(s => this.selectedOptions[s.id]).filter(o => o);

		const state = {
			version: 1,
			items: items,
			currentTab: this.currentTab,
			viewedTabs: this.viewedTabs,
			presetSource: this.presetSource,
			presetItems: this.presetItems,
			flipped: this.flipped,
			autoSelectOptions: this.autoSelectOptions,
		}

		if (this.selectedVariants.size > 0 || Object.keys(this.selectedOptions).length == 0) sessionStorage.setItem(this.stateId, JSON.stringify(state));
		return state;
	}

	saveState() {
		if (this.selectedVariants.size == 0 || Object.keys(this.selectedOptions).length == 0) return;
		if (this.lineItem) return;

		const state = this.getState();
		sessionStorage.setItem(this.stateId, JSON.stringify(state));
	}

	parseStateItems(items) {
		const selectors = this.getSelectors();
		const matchedSelectors = new Set();
		const selectedOptions = {};
		
		for (const item of items) {
			if (!item.Type) continue; // Item type needs to be present to match item to a selector
			const match = selectors.find(selector => !matchedSelectors.has(selector) && selector.types.find(type => type == item.Type || handlise(type).includes(item.Type)));

			if (match) {
				const matchedType = match.types.find(type => type == item.Type || handlise(type).includes(item.Type));
				matchedSelectors.add(match);
				selectedOptions[match.id] = { ... item, Type: matchedType };
			}
		}

		return selectedOptions;
	}

	async loadState() {
		this.stateLoading = true;

		const params = new URLSearchParams(window.location.search);

    const wishlistId = params.get('wishlist');
		const presetHandle = params.get('preset');
		const stateJson = sessionStorage.getItem(this.stateId);
		const config = params.get('config');
		const hotlink = params.get('hl');
		const lineItem = params.get('edit');

		if (lineItem) {
			const resp = await fetch('/cart?view=bundle-config');

			if (resp.ok) {
				const configs = await resp.json();
				const config = configs[lineItem];
				if (config) {
					this.selectedOptions = this.parseStateItems(config);
					this.lineItem = lineItem;
					this.currentTab = 'Summary';
				}
			}
		}
		else if (hotlink) {
			const key = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ2aXN1YWxpc2FyLWFwaS11c2VyIiwiaWF0IjoxNjg0ODc3MjE4LCJlbnYiOiJwcmUtcHJvZCJ9.lq_vO3DfkH0mjxwhvnhZYW2oXHgpO7Ubi-XOWLcrYuk';
			const resp = await fetch(`https://api.viewa.com/api/webar/hotlink/${hotlink}`, {
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${key}`
				}
			});

			if (!resp.ok) {
				console.error(`Could not load hotlink ${hotlink} (${resp.status})`);
			}
			else {
				const data = await resp.json();
				const items = JSON.parse(data.additionalData.items);
				this.selectedOptions = this.parseStateItems(items);
				this.currentTab = 'Summary';
			}
		}
		else if (config) {
			const items = config.split('.')
				.map(item => item.trim())
				.filter(item => item.length > 0)
				.map(item => item.split('_'))
				.map(item => ({ Type: item[0], Colour: item[1], Fabric: item[2], Size: item[3] }));
				
			this.loadPresetConfig(items);

			if (document.referrer.includes('/products/the-bed-threads-build-your-own-bundle')) this.presetSource = 'landing';
			if (params.get('utm_medium') == 'email') this.presetSource = 'email';
		}
		else if (wishlistId) {
			this.sessionId = wishlistId;

			// Wait for wishlist to load
			await new Promise(resolve => {
				const check = () => typeof window.wishlist === 'object' && window.wishlist.loaded ? resolve() : setTimeout(check, 250);
				check();
			});

			const items = window.wishlist.getItem(wishlistId).items;
			this.loadLegacySessionItems(items);
			this.currentTab = 'Summary';
		}
		else if (presetHandle) {
			const resp = await fetch(`/products/${presetHandle}?view=bundle-config`);
	
			if (resp.ok) {
				const config = await resp.json();
				
				window.addEventListener('onboardingcomplete', () => {
					this.loadPresetConfig(config);
					this.presetSource = 'preset';
				}, { once: true });
			}
		}
		else if (stateJson) {
			const state = JSON.parse(stateJson);

			if (state) {
				this.selectedOptions = this.parseStateItems(state.items);
				this.currentTab = state.currentTab;
				if (state.viewedTabs) this.viewedTabs = state.viewedTabs;
				this.presetSource = state.presetSource;
				this.presetItems = state.presetItems;
				this.flipped = state.flipped || {};
				this.autoSelectOptions = state.autoSelectOptions || {};
			}
		}

		this.stateLoading = false;
		this.updateRender();
		this.checkLoaded();
	}

	getConfigUrl() {
		const variants = this.getSelectedVariantsArray();
		const config = variants.map(variant => 
			`${handlise(variant.options.Type)}_${variant.options.Colour}_${variant.options.Fabric}${variant.options.Size ? `_${variant.options.Size}` : ''}`
		).join('.');

		const url = new URL(window.location.href);
		url.hash = '';
		url.search = '';
		url.searchParams.set('config', config);
		return url.toString();
	}

	async loadPresetConfig(config) {
		this.selectedOptions = this.parseStateItems(config);
		this.presetItems = this.getSelectedItemsArray();
	}

	loadLegacySessionItems(items) {
		const stateItems = [];

		for (const item of items) {
			const product = item.product.replace('-optional', '');
			const typeHandle = 
				item.type ? item.type : 
				product.includes('pillowcase') ? item.size.replace('-size', '-pillowcase') : 
				product.includes('duvet') ? 'duvet-cover' :
				product;

			// Convert from handlised type to proper type
			const type = typeHandle
				.replace(/-+/g, ' ')
				.toLowerCase()
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ');

			stateItems.push({
				Type: type,
				Colour: item.colour,
				Size: !item.product.includes('pillowcase') ? item.size : undefined,
				Fabric: item.fabric,
			});
		}
		
		this.selectedOptions = this.parseStateItems(stateItems);
		
		const firstOptionsEntry = Object.entries(this.selectedOptions).find(([key, _]) => this.config.tabs[0].id == key);

		if (firstOptionsEntry) {
			const [_, firstOptions] = firstOptionsEntry;

			this.autoSelectOptions = {
				Size: firstOptions.Size,
				Fabric: firstOptions.Fabric
			};
		}
	}

	getLegacySessionItems() {
		const items = [];
		const products = [];

		const buildVariantItem = (id, extra = false) => {
			const variant = this.selectedVariants.get(id);
			let type = handlise(variant.product.productType);
			let product = type;
			let category = 'required';

			if (type === 'duvet-cover') product = 'duvet';
			else if (type.includes('pillowcase')) product = 'pillowcase';
			else if (type.includes('sheet') && (extra || products.some(p => p.includes('sheet')))) product = 'sheet';

			if (extra) {
				category = 'optional';
				product = `${product}-optional`
			}
			else if (products.some(p => p.includes(product))) category = 'separates';

			const options = Object.fromEntries(
			 	variant.selectedOptions.map(option => [handlise(option.name), option.value])
			);

			const item = {
				variant: variant.id.replace('gid://shopify/ProductVariant/', ''),
				url: `/products/${variant.product.handle}`,
				sku: variant.sku,
				product: product,
				category: category,
				type: type,
				...options
			}

			products.push(product);
			items.push(item);
		}

		this.config.tabs.filter(tab => this.selectedVariants.has(tab.id)).forEach(tab => buildVariantItem(tab.id));
		this.config.extras.filter(extra => this.selectedVariants.has(extra.id)).forEach(extra => buildVariantItem(extra.id, true));

		return items;
	}

	restart() {
		this.autoSelectOptions = {};
		this.selectedOptions = {};
		this.flipped = {};
		this.viewedTabs = [];
		this.currentTab = this.config.tabs[0].name;
		this.presetSource = null;
		this.presetItems = null;

		sessionStorage.removeItem(this.stateId);
		sessionStorage.removeItem(`bundleImage${theme.product.id}`);
		this.dispatchEvent(new CustomEvent('bundle:restart', { bubbles: true, composed: true }));
		document.querySelector('bundle-onboarding')?.open();
		this.scrollContentTo(0);
	}

	querySelectorSlot(selector) {
		return this.querySelectorAllSlot(selector)[0];
	}

	querySelectorAllSlot(selector) {
		const nodes = [];

		for (const slot of this.shadowRoot.querySelectorAll('slot')) {
			for (const elem of slot.assignedElements({flatten: true})) {
				if (elem.matches(selector)) nodes.push(elem);
				else nodes.push(...elem.querySelectorAll(selector));
			}
		}

		return nodes;
	}

	scrollContentTo(target) {
		if (!this.offsetParent) return;

		// Delay to allow for transition from fullscreen
		// eg, when clicking on 'View Summary' button while fullscreen
		const animationDelay = this.fullscreen ? 300 : 0;
		const content = this.shadowRoot.querySelector('.content');
		const elem = window.innerWidth < 992 ? content : document.documentElement;

		const getDistance = () => {
			const targetScroll = typeof target == 'function' ? target() : target;
			if (window.innerWidth > 992) return Math.min(targetScroll, Math.max(0, content.clientHeight - (window.innerHeight - 180))) - elem.scrollTop;
			return targetScroll - elem.scrollTop;
		}

		setTimeout(() => {
			let start = null;
			const duration = window.innerWidth < 992 ? Math.max(450, Math.abs(getDistance()) * 4) : (target == 0 ? 300 : 450);

			const easeInOutQuad = (x) => x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

			const animate = timestamp => {
				if (!start) start = timestamp;
				document.documentElement.style.scrollBehavior = 'auto';
				document.body.style.scrollBehavior = 'auto';

				const elapsed = timestamp - start;

				const distance = getDistance();
				elem.scrollTop += distance * easeInOutQuad(Math.min(elapsed / duration, 1));

				if (elapsed < duration) requestAnimationFrame(animate);
				else {
					document.documentElement.style.scrollBehavior = '';
					document.body.style.scrollBehavior = '';
				}
			}

			requestAnimationFrame(animate);
		}, animationDelay)
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('fullscreen')) document.body.classList.toggle('bundle-fullscreen', this.fullscreen);

		// Sort pillowcase step to second position
		const pillowcaseStep = this.config.tabs.find(tab => tab.name.includes('Pillowcase'));
		if (pillowcaseStep) {
			const pillowcaseIndex = this.config.tabs.indexOf(pillowcaseStep);
			this.config.tabs.splice(pillowcaseIndex, 1);
			this.config.tabs.splice(1, 0, pillowcaseStep);
		}

		// Scroll current step to top
		if (changedProperties.has('currentStep')) {
			if (this.currentStep == null) this.scrollContentTo(0);
			else {
				const target = this.shadowRoot.getElementById(`step-${handlise(this.currentStep)}`);
				if (target) this.scrollContentTo(() => target.offsetTop);
			}
		}

		this.bundleValid = this.requiredComplete() && this.extrasComplete();
		if (this.currentTab == null) this.currentTab = this.config.tabs[0].name;

		if (!this.viewedTabs.includes(this.currentTab)) {
			this.viewedTabs = [...this.viewedTabs, this.currentTab];
		}

		window.bundleBuilderTab = this.currentTab;
		this.currentSelector = this.config.tabs.find(tab => tab.name == this.currentStep)?.id;
		this.showFlipToggle = false;
	
		if (this.selectedOptions[this.currentSelector]) {
			const currentOptions = this.selectedOptions[this.currentSelector];
			if ((currentOptions['Type'] == 'Quilt' || currentOptions['Type'] == 'Muslin Throw') && currentOptions['Colour'] && currentOptions['Colour'].includes('&')) {
				this.showFlipToggle = true;
			}	
		}

		document.body.classList.toggle('bundle-valid', this.bundleValid);

		if (this.bundleValid || Object.keys(this.selectedOptions).length == 0) {
			for (let button of this.querySelectorAllSlot('wishlist-add-button-byob')) {
				button.bundleId = this.sessionId;
				button.bundleItems = this.getLegacySessionItems();
				button.dataset.discount = this.getDiscount();
			}
		}

		this.saveState();
	}

	updated(changedProperties) {
		if (changedProperties.has('currentTab')) {
			this.shadowRoot.querySelector('.content').scrollTop = 0;
		}
	}

	isReady() {
		return this.loadedSelectors.has(this.config.tabs[0].id);
	}

	isSessionReady() {
		return this.isReady() && Object.keys(this.selectedOptions).every(id => this.loadedSelectors.has(id))  && (!this.stateLoading);
	}

	checkLoaded(selectorId = undefined) {
		if (selectorId) {
			this.loadedSelectors.add(selectorId);
			console.log('Loaded selector ' + selectorId);
		}

		if (!this.ready && this.isReady()) {
			const hasSession = Object.keys(this.selectedOptions).length > 0 || this.wishlistId;

			this.dispatchEvent(
				new CustomEvent('bundlebuilderready', { 
					bubbles: true, 
					composed: true, 
				})
			);

			console.log('Bundle builder ready');
			trackEvent('BYOB Ready', { time: performance.now() / 1000 });
			window.bundleBuilderReady = true;
			this.ready = true;
		}

		if (!this.sessionReady && this.isSessionReady()) {
			this.dispatchEvent(
				new CustomEvent('bundlebuildersessionready', { 
					bubbles: true, 
					composed: true
				})
			);

			console.log('Bundle builder session ready');
			window.bundleBuilderSessionReady = true;
			this.sessionReady = true;
		}

		if (!this.loaded && this.ready && this.loadedSelectors.size == this.config.tabs.length + this.config.extras.length) {
			this.dispatchEvent(new CustomEvent('bundlebuilderload', { bubbles: true, composed: true }));
			console.log('Bundle builder fully loaded');
			trackEvent('BYOB Loaded', { time: performance.now() / 1000 });
			this.loaded = true;
		}
	}

	async updateRender() {
		await this.updateComplete;

		const variants = [];

		// Add selected required variants, or default to white
		for (const [idx, tab] of this.config.tabs.entries()) {
			const selected = this.selectedVariants.get(tab.id);

			if (selected) {
				variants.push({
					type: selected.product.productType,
					colour: selected.selectedOptions.find(option => option.name == 'Colour').value,
					flipped: this.flipped[tab.id] || false,
					fabric: selected.selectedOptions.find(option => option.name == 'Fabric').value,
				})
			}
			else if (!this.tieredDiscount || idx < 2 || tab.name == 'Fitted Sheet') {
				const type = this.selectedOptions[tab.id] && this.selectedOptions[tab.id]['Type'] ? this.selectedOptions[tab.id]['Type'] : tab.types[0];
				const colour = this.selectedOptions[tab.id] && this.selectedOptions[tab.id]['Colour'] ? this.selectedOptions[tab.id]['Colour'] : 'White';
				const fabric = this.selectedOptions[tab.id] && this.selectedOptions[tab.id]['Fabric'] ? this.selectedOptions[tab.id]['Fabric'] : 'Linen';
				variants.push({type: type, colour: colour, fabric: fabric});
			}
		}

		// Add any selected or active extra variants
		for (const extra of this.config.extras) {
			const selected = this.selectedVariants.get(extra.id);
			
			if (selected && selected.selectedOptions.find(option => option.name == 'Colour')) {
				variants.push({
					type: selected.product.productType,
					colour: selected.selectedOptions.find(option => option.name == 'Colour').value,
					flipped: this.flipped[extra.id] || false,
					fabric: selected.selectedOptions.find(option => option.name == 'Fabric').value,
				})
			}
			else if (this.selectedOptions[extra.id] != undefined) {
				const type = this.selectedOptions[extra.id] && this.selectedOptions[extra.id]['Type'] ? this.selectedOptions[extra.id]['Type'] : extra.types[0];
				const colour = this.selectedOptions[extra.id] && this.selectedOptions[extra.id]['Colour'] ? this.selectedOptions[extra.id]['Colour'] : 'White';
				const fabric = this.selectedOptions[extra.id] && this.selectedOptions[extra.id]['Fabric'] ? this.selectedOptions[extra.id]['Fabric'] : 'Linen';
				variants.push({type: type, colour: colour, fabric: fabric});
			}
		}

		this.querySelectorSlot('[render-config]').setAttribute('render-config', JSON.stringify({variants: variants}));
	}

	render() {
		return html`
			<main>
				<div class="render">
					<slot name="render"></slot>
					<bundle-checkbox 
						?hidden=${!this.showFlipToggle}
						label="Flip my Quilt!"
						?checked=${this.flipped[this.currentSelector]} 
						@change=${e => this.setFlip(this.currentSelector, e.target.checked)}
					></bundle-checkbox>
					<button class="fullscreen" 
						data-fs-element="${this.fullscreen ? 'BYOB Close Fullscreen' : 'BYOB Open Fullscreen'}" 
						aria-label="${this.fullscreen ? 'Close Fullscreen' : 'Open Fullscreen'}" 
						@click=${() => {
							if (this.currentStep == 'Summary') this.querySelector('.bundle__image[data-active-item="true"]')?.click();
							else this.fullscreen = !this.fullscreen;
						}}
					>
						${this.fullscreen ? html`
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M4 9H9V4" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M20 9H15V4" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M4 15H9V20" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M20 15H15V20" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>						
						`: html`
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M9 4H4V9" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M15 4H20V9" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M9 20H4V15" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M15 20H20V15" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>						
						`}
					</button>
				</div>
				<div class="ui" data-tab="${this.currentTab}" step="${this.currentStep ?? ''}" 
					@click=${() => this.fullscreen = false}
				>
					<div class="content">
						<div class="header">
							<h1>${this.productTitle}</h1>
							${this.tieredDiscount ? html`
								<button class="tier-info-button button button--secondary" @click=${() => document.getElementById('bundle-tier-modal').open()} data-fs-element="BYOB How It Works Button">How it works</button>
							` : ''}
								<bundle-restart-button label="Restart" .tooltip=${false}></bundle-restart-button>
							<p class="subtitle">
							${this.autoSelectOptions['Fabric'] == 'Cotton' ? 
								(theme.storeLocation == 'US' ? 'Organic Cotton Percale' : 'Organic Cotton') + (window.cottonComingSoon ? ' (Launching July 15)' : '') : 
								'100% French Flax Linen'
							}
							</p>
						</div>
						${this.config.tabs.map((tab, idx) => html`
							<bundle-step
								id="step-${handlise(tab.name)}"
								?disabled=${this.selectedVariants.size < idx}
								name=${tab.name}
								step=${idx + 1}
								?prevSelected=${this.selectedVariants.has(this.config.tabs[idx - 1]?.id)}
								?nextSelected=${this.selectedVariants.has(this.config.tabs[idx + 1]?.id)}
								.selectedVariant=${this.selectedVariants.get(tab.id)}
								tier=${this.tieredDiscount ? (this.tiers[idx] || 0) : 0}
								@next=${this.nextTab}
								@viewed=${() => { if(!this.viewedTabs.includes(tab.name)) this.viewedTabs = [...this.viewedTabs, tab.name]; }}
								@remove=${() => {
									this.removeVariant(tab.id);
									trackEvent('BYOB Remove Item', { step: tab.name });
								}}
								@open=${() => this.currentStep = tab.name}
								@close=${() => this.currentStep = this.currentStep == tab.name ? null : this.currentStep}
							>
							${keyed(tab.id, html`
								<bundle-variant-selector 
									@variantchange=${e => this.selectVariant(tab.id, e.detail)} 
									@optionchange=${e => this.selectOptions(tab.id, e.detail)}
									@flipchange=${e => this.setFlip(tab.id, e.detail)}
									@load=${() => this.checkLoaded(tab.id)}
									@remove=${() => this.removeVariant(tab.id)}
									.autoSelectOptions=${this.autoSelectOptions}
									.collections=${tab.collections}
									.selectedOptions=${{...this.selectedOptions[tab.id]}}
									.preOrderMonth=${this.preOrderMonth}
									.productName=${tab.name}
									.hiddenColours=${this.config.hiddenColours}
									.sizeGuide=${this.sizeGuide}
									.flipped=${this.flipped[tab.id]}
									?tieredDiscount=${this.tieredDiscount}
									step=${idx + 1}
								>
									<slot name="type-info-${tab.id}" slot="type-info"></slot>
								</bundle-variant-selector>
							`)}
							</bundle-step>
						`)}

						<bundle-step
							id="step-summary"
							name=${this.bundleValid ? 'View Summary' : 'Summary'}
							subtitle=${this.bundleValid ? `${this.selectedVariants.size} items` : ''}
							tag=${this.bundleValid && this.getDiscount() > 0 ? `${this.getDiscount() * 100}% off` : ''}
							?active=${this.currentTab == 'Summary'}
							?disabled=${!this.bundleValid}
							?underline=${this.bundleValid}
							@viewed=${() => { if(!this.viewedTabs.includes('Summary')) this.viewedTabs = [...this.viewedTabs, 'Summary']; }}
							@open=${() => this.currentStep = 'Summary'}
							@close=${() => this.currentStep = this.currentStep == 'Summary' ? null : this.currentStep}
						>
							<bundle-summary 
								@addtocart=${this.onAddToCart}
								.discount=${this.getDiscount() || 0}
								.currencySymbol=${this.currencySymbol}
								.currency=${this.currency}
								.priceList=${this.config.priceList ? this.config.priceList : {}}
								.parentId=${this.parentId}
								.parentTitle=${this.productTitle}
								.parentSku=${this.parentSku}
								lineItem=${this.lineItem}
								.presetSource=${this.presetSource}
								.preorderShippingText=${this.config.preorderShippingText}
								.bundleImage=${this.bundleImage}
								.valid=${this.bundleValid}
								.required=${this.config.tabs
									.filter(tab => this.selectedVariants.has(tab.id))
									.map(tab => ({ tab: tab.name, id: tab.id, variant: this.selectedVariants.get(tab.id)}))
								}
								.extras=${this.config.extras
									.filter(extra => this.selectedVariants.has(extra.id))
									.map(extra => ({ tab: 'Extras', id: extra.id, variant: this.selectedVariants.get(extra.id)}))
								}
							>
								<slot name="promo-pill" slot="promo-pill"></slot>
								<slot name="summary-footer-items" slot="footer-items"></slot>
							</bundle-summary>
						</bundle-step>

						${this.tieredDiscount ? html`
							<button class="tier-info-button button button--secondary" @click=${() => document.getElementById('bundle-tier-modal').open()} data-fs-element="BYOB How It Works Button">How it works</button>
						`: ''}

						<bundle-restart-button textColour="#575757" iconColour="#767676" fontSize="14px"></bundle-restart-button>
						<slot name="more-info"></slot>
					</div>
				</div>
				${this.tieredDiscount || this.config.discount > 0 ? html`
					<bundle-summary-bar 
						@addtocart=${this.onAddToCart}
						.discount=${this.getDiscount() || 0}
						.tiers=${this.tieredDiscount ? this.tiers : []}
						.currencySymbol=${this.currencySymbol}
						.currency=${this.currency}
						.priceList=${this.config.priceList ? this.config.priceList : {}}
						.parentId=${this.parentId}
						.parentTitle=${this.productTitle}
						.parentSku=${this.parentSku}
						lineItem=${this.lineItem}
						.text=${this.config.summaryBarText}
						.presetSource=${this.presetSource}
						.preorderShippingText=${this.config.preorderShippingText}
						.bundleImage=${this.bundleImage}
						.valid=${this.bundleValid}
						.required=${this.config.tabs
							.filter(tab => this.selectedVariants.has(tab.id))
							.map(tab => ({ tab: tab.name, id: tab.id, variant: this.selectedVariants.get(tab.id)}))
						}
						.extras=${this.config.extras
							.filter(extra => this.selectedVariants.has(extra.id))
							.map(extra => ({ tab: 'Extras', id: extra.id, variant: this.selectedVariants.get(extra.id)}))
						}
						?hidden=${(this.viewedTabs.length <= 1 && this.selectedVariants.size < 2) || (this.currentStep == 'Summary' && !this.modalOpen)}
						@click=${() => this.fullscreen = false}
						currentStep=${this.currentStep}
						?forceUpdate=${this.modalOpen}
						stepsComplete=${this.selectedVariants.size}
						stepsTotal=${this.config.tabs.length}
					></bundle-summary-bar>
				`: ''}
			</main>
			<slot></slot>
		`
	}

	static get styles() {
		return css`
			${buttonStyles}

			main {
				display: flex;
				flex-direction: column;
				max-width: 1280px;
				position: fixed;
				max-height: 100vh;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				line-height: 1.3;
				font-weight: 400;
				background: white;
				z-index: 5;
			}

			.header {
				display: none;
			}

			:host {
				--mobile-render-height: max(30vh, 294px);
				--mobile-render-height: max(30dvh, 294px);
			}

			:host([currentstep="Summary"]) {
				--mobile-render-height: max(30vh, 294px);
				--mobile-render-height: max(30dvh, 294px);
			}

			:host([fullscreen]) {
				--mobile-render-height: calc(100% - 120px);
			}

			@media screen and (max-width: 512px) and (max-height: 680px) {
				:host {
					--mobile-render-height: max(30vh, 245px);
					--mobile-render-height: max(30dvh, 245px);
				}

				:host([fullscreen]) {
					--mobile-render-height: calc(100% - 105px);
				}
			}

			.render {
				position: relative;
				background-color: rgb(243, 243, 243);
				display: flex;
				align-items: center;
				transition: height 300ms;
				height: var(--mobile-render-height);
			}

			.render bundle-checkbox {
				position: absolute;
				left: 16px;
				bottom: 16px;
				transition: opacity 150ms;
			}

			.render bundle-checkbox[hidden] {
				opacity: 0;
				pointer-events: none;
			}

			.fullscreen {
				position: absolute;
				bottom: 12px;
				right: 8px;
				padding: 4px;
				background: none;
				border: none;
				display: none;
				z-index: 15;
			}

			.fullscreen svg {
				display: block;
				padding: 4px;
				border-radius: 50%;
				transition: background-color 300ms, box-shadow 300ms;
			}

			.fullscreen::after {
				content: 'zoom in';
				position: absolute;
				bottom: -4px;
				left: 50%;
				transform: translateX(-50%);
				font: var(--text-sm);
				color: var(--color-text-tertiary, #575757);
				white-space: nowrap;
			}

			:host([fullscreen]) .fullscreen::after {
				content: 'zoom out';
			}

			:host([currentstep="Summary"]) .fullscreen svg {
				background: var(--color-bg-primary, #FFF);
				box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.14);
			}

			.ui {
				flex: 1;
				min-height: 0;
				transition: opacity 300ms;
				display: grid;
				grid-template-columns: 100%;
				grid-template-rows: 1fr;
			}

			@media screen and (max-width: 992px) {
				.fullscreen {
					display: inline-block;
				}	

				:host([fullscreen]) .ui {
					height: 120px;
					opacity: 0.3;
				}

				:host([fullscreen]) .content {
					pointer-events: none;
				}
			}

			.content {
				overflow-y: auto;
				overflow-x: hidden;
				position: relative;
				padding: 0;
				padding-bottom: 120px;
			}

			bundle-step ~ bundle-restart-button {
				display: block;
				margin-top: 12px;
			}

			.content::-webkit-scrollbar {
        width: 5px;
        display: block;
      }

			.content::-webkit-scrollbar-track {
        box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        display: block;
      }

      .content::-webkit-scrollbar-thumb {
        background-color: darkgrey;
        display: block;
      }

			bundle-variant-selector {
				margin-bottom: var(--padding-sm);
			}

			bundle-summary {
				margin-bottom: 24px;
			}

			.tier-info-button {
				border: none;
				display: flex;
				gap: 8px;
				align-items: center;
				justify-content: center;
				padding: 8px;
				font-size: 14px;
			}

			.tier-info-button::before {
				content: '';
				display: block;
				width: 16px;
				height: 16px;
				margin-block: -4px;
				background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z' stroke='%23575757' stroke-width='0.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9 9C9 5.49997 14.5 5.5 14.5 9C14.5 11.5 12 10.9999 12 13.9999' stroke='%23575757' stroke-width='0.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M12 18.0095L12.0095 17.9989' stroke='%23575757' stroke-width='0.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
				background-size: 100%;
			}

			bundle-step ~ .tier-info-button {
				width: 100%;
				margin-top: 16px;
				color: var(--color-button-text-tertiary, #575757) !important;
			}

			bundle-step ~ .tier-info-button::after {
				content: '';
				display: block;
				width: 16px;
				height: 16px;
			}

			@media screen and (min-width: 992px) {
				main {
					position: static;
					display: grid;
					grid-template-columns: 52% 1fr;
					grid-template-rows: min-content 1fr;
					column-gap: 64px;
					margin: 0 auto;
					margin-top: 24px;
					padding: 0 32px;
					max-height: unset;
				}

				/* Add negative margin and increase padding to allow tooltips to overflow into tab selector */
				.content {
					margin-inline: -16px;
					padding-inline: 16px;
				}

				.header {
					display: grid;
					margin-bottom: var(--padding-md);
					grid-template-columns: 1fr auto;
					grid-auto-flow: column dense;
				}

				.header bundle-restart-button,
				.header .tier-info-button {
					grid-column: 2;
					justify-self: end;
				}

				h1 {
					margin: 0;
					margin-bottom: 8px;
					font: var(--title-lg);
					grid-column: 1;
				}

				.subtitle {
					font: var(--text-md);
					margin: 0;
					color: var(--color-text-tertiary, #575757);
					margin-right: var(--padding-sm);
					grid-column: 1;
				}

				.ui {
					height: unset !important;
					position: relative; /* For more info modals */
					grid-template-columns: 1fr;
				}

				.render {
					grid-column: 1;
					grid-row: 1 / span 2;
					align-self: start;
					transition: none;
					height: calc(100vh - var(--header-height) - 32px - 80px);
					position: sticky;
					top: calc(var(--header-height) + 20px);
				}

				.render bundle-checkbox {
					left: 20px;
					bottom: 20px;
				}

				.content {
					padding-top: 8px;
					height: auto;
					box-sizing: border-box;
					padding-bottom: 60px;
				}

				.ui[data-tab="Summary"] .content {
					height: unset;
					overflow-y: visible;
				}

				.content bundle-step ~ bundle-restart-button,
				.content bundle-step ~ .tier-info-button {
					display: none;
				}

				bundle-variant-selector {
					margin-bottom: 16px;
				}
			}
		`;
	}
}

customElements.define('bundle-builder', BundleBuilder);


class BundleRestartButton extends LitElement {
	static get properties() {
		return {
			label: { type: String },
			tooltip: { type: Boolean },
			active: { type: Boolean },
			iconColour: { type: String },
			textColour: { type: String },
			fontSize: { type: String }
		}
	}

	constructor() {
		super();
		this.label = 'Start Your Bundle Again';
		this.tooltip = true;
		this.active = false;
		this.textColour = 'inherit';
		this.iconColour = 'inherit';
		this.fontSize = '16px';
	}


	restart() {
		this.active = false;
		this.dispatchEvent(new CustomEvent('restart', { bubbles: true, composed: true }));
		fireEvent('Restart-Bundle');
		trackEvent('BYOB Restarted');
	}

	render() {
		return html`
			<button class="button button--secondary" name="restart" @click=${() => this.active = true} 
				style="
					--text-colour: ${this.textColour}; 
					--icon-colour: ${this.iconColour};
					--font-size: ${this.fontSize};
					">
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M3.18949 10.9735C4.61906 13.3014 7.57395 14.2971 10.1649 13.1973C13.0193 11.9857 14.351 8.68953 13.1394 5.83512C11.9278 2.98071 8.6316 1.64896 5.77719 2.86059C4.66184 3.33403 3.77898 4.12573 3.18949 5.085" stroke="#767676" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M5.75378 5.17772L3.57643 5.17772C3.22849 5.17772 2.94643 4.89566 2.94643 4.54772L2.94643 2.37036" stroke="#767676" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>				
				${this.label}
				${this.tooltip ? html`<span class="tooltip"><label>Remove all selections so far</label></span>` : ''}
			</button>
			<div class="modal" data-active=${this.active}>
				<div class="modal-content">
					<p>Are you sure? The selections you've made so far won't be saved.</p>
					<button name="confirm" @click=${this.restart}>Yes, Start Again</button>
					<button name="cancel" @click=${() => this.active = false}>No, Cancel</button>
					<button name="close" @click=${() => this.active = false}>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path fill-rule="evenodd" clip-rule="evenodd" d="M10.9402 11.9991L5.46974 17.4681L6.53024 18.5301L12.0007 13.0596L17.4697 18.5301L18.5317 17.4696L13.0612 11.9991L18.5317 6.5301L17.4697 5.4696L12.0007 10.9386L6.53174 5.4696L5.46974 6.5301L10.9402 11.9991Z" fill="#212121"></path>
						</svg>
					</button>
				</div>
			</div>
		`;
	}

	static get styles() {
		return css`
			${buttonStyles}

			button[name="restart"] {
				font: inherit;
				color: var(--text-colour, inherit) !important;
				display: block;	
				border: none;
				background-color: white;
				width: 100%;
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 8px;
				transition: background-color 150ms;
				gap: 8px;
				white-space: nowrap;
				font-size: var(--font-size, 16px);
			}

			button[name="restart"] path {
				stroke: var(--icon-colour, #212121);
			}

			button[name="restart"]:after {
				display: block;
				content: '';
				width: 16px;
			}

			.tooltip {
				display: none;
				width: 16px;
				height: 16px;
				background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.00006 13.44C4.99206 13.44 2.56006 11.008 2.56006 8C2.56006 4.992 4.99206 2.56 8.00006 2.56C11.0081 2.56 13.4401 4.992 13.4401 8C13.4401 11.008 11.0081 13.44 8.00006 13.44ZM8.00006 3.2C5.34406 3.2 3.20006 5.344 3.20006 8C3.20006 10.656 5.34406 12.8 8.00006 12.8C10.6561 12.8 12.8001 10.656 12.8001 8C12.8001 5.344 10.6561 3.2 8.00006 3.2Z' fill='black'%3E%3C/path%3E%3Cpath d='M6.33601 6.27199C6.43201 6.01599 6.52801 5.82399 6.72001 5.66399C6.88001 5.50399 7.07201 5.37599 7.32801 5.27999C7.58401 5.18399 7.84001 5.15199 8.12801 5.15199C8.35201 5.15199 8.57601 5.18399 8.76801 5.24799C8.96001 5.31199 9.15201 5.40799 9.31201 5.53599C9.47201 5.66399 9.60001 5.82399 9.66401 6.01599C9.76001 6.20799 9.79201 6.43199 9.79201 6.65599C9.79201 6.97599 9.72801 7.23199 9.60001 7.45599C9.47201 7.67999 9.28001 7.87199 9.08801 8.09599L8.67201 8.51199C8.57601 8.60799 8.48001 8.70399 8.44801 8.79999C8.38401 8.89599 8.35201 9.02399 8.35201 9.15199C8.32001 9.27999 8.32001 9.37599 8.32001 9.63199H7.80801C7.80801 9.37599 7.80801 9.27999 7.84001 9.08799C7.87201 8.92799 7.93601 8.76799 8.00001 8.60799C8.06401 8.47999 8.16001 8.35199 8.28801 8.22399C8.41601 8.09599 8.57601 7.96799 8.73601 7.77599C8.89601 7.61599 9.02401 7.45599 9.12001 7.29599C9.21601 7.13599 9.28001 6.91199 9.28001 6.71999C9.28001 6.55999 9.24801 6.39999 9.18401 6.27199C9.12001 6.14399 9.02401 6.01599 8.92801 5.91999C8.83201 5.82399 8.70401 5.75999 8.54401 5.69599C8.38401 5.63199 8.25601 5.59999 8.09601 5.59999C7.87201 5.59999 7.68001 5.63199 7.52001 5.72799C7.36001 5.79199 7.20001 5.91999 7.10401 6.04799C7.00801 6.17599 6.91201 6.33599 6.84801 6.52799C6.78401 6.71999 6.72001 6.81599 6.72001 7.03999H6.20801C6.20801 6.75199 6.24001 6.52799 6.33601 6.27199ZM8.32001 10.24V10.88H7.68001V10.24H8.32001Z' fill='black'%3E%3C/path%3E%3C/svg%3E");
				pointer-events: none;
				position: relative;
			}

			.tooltip label {
				position: absolute;
				opacity: 0;
				position: absolute;
				bottom: calc(100% + 8px);
				background: var(--color-bg-inverted-primary);
				padding: 0 10px;
				left: 8px;
				transform: translateX(-50%);
				opacity: 0;
				border-radius: 2px;
				white-space: nowrap;
				transition: opacity .15s cubic-bezier(.46,.03,.52,.96);
				font-size: 14px;
				width: auto;
				height: auto;
				color: #fff;
				font-weight: 400;
				pointer-events: none;
				user-select: none;
			}

			.tooltip label::before {
				content: "";
				position: absolute;
				display: block;
				width: 0px;
				left: 50%;
				bottom: 4px;
				border: 8px solid transparent;
				border-bottom: 0;
				border-top: 8px solid #212121;
				transform: translate(-50%,100%);
			}

			@media (hover: hover) {
				button[name="restart"]:after {
					display: none;
				}

				button[name="restart"]:hover .tooltip label {
					opacity: 1;
				}

				.tooltip {
					display: block;
				}
			}

			@keyframes fade {
				0% { opacity: 0; } 
				100% { opacity: 1; }
			}

			@keyframes fly {
				0% { transform: translateY(25px); } 
				100% { transform: translateY(0); }
			}

			.modal {
				position: fixed;
				left: 0;
				top: 0;
				bottom: 0;
				right: 0;
				background-color: rgba(229, 229, 229, 0.3);
				z-index: 9999;
				align-items: center;
				justify-content: center;
				padding: 14px;
				transition: opacity 250ms;
				display: none;
			}

			.modal[data-active="true"] {
				display: flex;
				animation: fade 250ms both;
			}

			.modal-content {
				background-color: white;
				font-size: 16px;
				line-height: 21px;
				font-weight: 400;
				display: grid;
				grid-template-columns: 1fr;
				gap: 8px;
				justify-content: center;
				padding: 32px;
				border-radius: 4px;
				box-shadow: 0px 0px 8px rgba(120, 120, 120, 0.18);
				width: 100%;
				transition: transform 250ms;
				position: relative;
				transform: translateY(25px);
				justify-items: center;
				animation: fly 250ms both;
			}

			.modal-content > *:not(button[name="close"]) {
				width: 204px;
			}

			button {
				cursor: pointer;
			}

			p {
				margin: 0;
				text-align: center;
				font-size: 0.9375em;
				margin-bottom: 8px;
			}

			button[name="close"] {
				position: absolute;
				right: 8px;
				top: 8px;
				cursor: pointer;
				transition: opacity 150ms;
				display: block;
				background: none;
				padding: 0;
				margin: 0;
				border: none;
			}

			button[name="confirm"], button[name="cancel"] {
				font: inherit;
				background: none;
				border: none;
				border-radius: 2px;
				padding: 10px 20px;
				display: block;
				text-transform: none;
			}

			button[name="confirm"] {
				background-color: #212121;
				color: white;
			}

			button[name="cancel"] {
				border: 1px solid #e1e1e1;
				color: var(--color-text-primary);
			}

			button[name="confirm"]:hover {
				opacity: 0.9;
			}

			button[name="cancel"]:hover {
				background: var(--button-bg-hover);
			}

			@media screen and (min-width: 512px) {
				.modal-content {
					grid-template-columns: 1fr 1fr;
					padding: 32px 60px;
					width: unset;
				}

				.modal-content > *:not(button[name="close"]) {
					width: auto;
					max-width: 320px;
					justify-self: stretch;
				}

				p {
					grid-column: 1 / span 2;
					font-size: 16px;
					line-height: 21px;
				}

				button[name="cancel"] {
					grid-column: 1;
					grid-row: 2;
				}	

				button[name="confirm"] {
					grid-column: 2;
					grid-row: 2;
				}
			}

			@media screen and (min-width: 768px) {
				button[name="restart"] {
					font-size: 14px;
				}
			}
		`;
	}
}

customElements.define('bundle-restart-button', BundleRestartButton);

class BundleSummaryBase extends LitElement {
	static get properties() {
		return {
			required: { type: Array },
			extras: { type: Array },
			valid: { type: Boolean },
			currencySymbol: { type: String },
			currency: { type: String },
			discount: { type: Number },
			priceList: { type: Object },
			parentId: { type: String },
			parentTitle: { type: String },
			parentSku: { type: String },
			bundleImage: { type: String },
			lineItem: { type: String }
		}
	}

	constructor() {
		super();
		this.required = [];
		this.extras = [];
		this.valid = false;
		this.currencySymbol = '';
		this.currency = '';
		this.discount = 0.2;
		this.automaticDiscount = theme.automaticDiscount ? theme.discountAmount / 100 : 0;
		this.hasPreorder = false;
		this.preorderShippingText = '';
		this.lineItem = '';

		window.onFeatureEnabled('hide-trailing-zeroes', () => this.setAttribute('hide-trailing-zeroes', ''));
	}

	willUpdate() {
		this.subTotalCents = 
			this.required.reduce((acc, item) => acc + this.getVariantOriginalCents(item.variant), 0) +
			this.extras.reduce((acc, item) => acc + this.getVariantOriginalCents(item.variant), 0);

		this.totalCents = 
			this.required.reduce((acc, item) => acc + this.getVariantDiscountedCents(item.variant), 0) +
			this.extras.reduce((acc, item) => acc + this.getVariantDiscountedCents(item.variant), 0);

		this.automaticDiscountCents = 
			this.required.reduce((acc, item) => acc + this.getVariantAutomaticDiscountCents(item.variant), 0) +
			this.extras.reduce((acc, item) => acc + this.getVariantAutomaticDiscountCents(item.variant), 0);

		this.extraDiscountCents = 
			this.required.reduce((acc, item) => acc + this.getVariantExtraDiscountCents(item.variant), 0) +
			this.extras.reduce((acc, item) => acc + this.getVariantExtraDiscountCents(item.variant), 0);

		this.saleDiscountCents = this.extraDiscountCents + this.automaticDiscountCents;
		this.bundleDiscountCents = this.subTotalCents - this.totalCents;
		this.totalCentsDisplay = this.totalCents - this.saleDiscountCents;

		this.hasPreorder = this.required.some(item => item.variant.currentlyNotInStock) || this.extras.some(item => item.variant.currentlyNotInStock);
	}

	getVariantOriginalCents(variant) {
		if (variant.priceTests && window.getActivePriceTest()) {
			const price = variant.priceTests[window.getActivePriceTestIndex()];
			return price * 100;
		}
		if (this.priceList[variant.sku]) return this.priceList[variant.sku];
		else return parseFloat(variant.price.amount) * 100;
	}

	getVariantDiscountedCents(variant) {
		const discount = variant.discountOverride && variant.discountOverride > 0 ? variant.discountOverride : this.discount;
		const cents = Math.min(this.getVariantOriginalCents(variant) * (1 - discount), parseFloat(variant.price.amount) * 100);
		
		// Floor to nearest 5 cents
		return Math.floor(cents / 5) * 5;
	}

	getVariantExtraDiscountCents(variant) {
		if (theme.b2b) return 0;
		if (variant.extraDiscount) return this.getVariantDiscountedCents(variant) * variant.extraDiscount;
		return 0;
	}

	getVariantAutomaticDiscountCents(variant) {
		if (theme.b2b) return 0;
		if (this.automaticDiscount > 0) return (this.getVariantDiscountedCents(variant) - this.getVariantExtraDiscountCents(variant)) * this.automaticDiscount;
		return 0;
	}

	formatMoney(cents) {
		const money = (cents / 100).toFixed(2);
		return html`${this.currencySymbol}${money.includes('.00') ? html`${money.replace('.00', '')}<span class="zeroes">.00</span>` : money}`;
	}

	async addToCart() {
		const bundleId = Date.now();
		const cartItems = [];
		this.shadowRoot.querySelector('button[name="atc"]').classList.add('loading');

		const image = this.bundleImage;
		let imageUrl = null;

		if (image && image.includes('base64') && window.featureEnabled && window.featureEnabled('bundle-cart-image')) {
			const resp = await fetch('https://bundle-images.bedthreads.workers.dev/', {
				method: 'POST',
				body: JSON.stringify({image: image})
			});

			if (resp.ok) {
				const json = await resp.json();
				if (json.url) imageUrl = json.url;
			}
		}

		if (window.theme.bxgy && window.theme.bxgy.targetProductHandles.some(handle => handle.includes('build-your-own'))) {
			cartItems.push({
				id: window.theme.bxgy.giftVariantId, 
				quantity: 1, 
				properties: { 
					'_FreeGift': true, 
					'_BXGY': true 
				}
			});
		}

		// Add child items
		const variants = this.required.map(item => item.variant).concat(this.extras.map(item => item.variant));
		
		for (const [idx, variant] of variants.entries()) {
			const originalCents = this.getVariantOriginalCents(variant);
			const discountedCents = this.getVariantDiscountedCents(variant);
			const extraDiscountCents = this.getVariantExtraDiscountCents(variant);
			const preorderProps = variant.currentlyNotInStock ? { PreOrder: `${variant.preOrderMonth} delivery` } : {};

			let bundleProps = {
				_PartOf: this.parentTitle,
				_BundleId: `${this.parentId}/${bundleId}`,
				_BundleSKU: this.parentSku,
			} 

			if (imageUrl) bundleProps['_BundleImage'] = imageUrl;

			if (variant.priceTests && window.getActivePriceTest()) {
				bundleProps['_PriceTest'] = window.getActivePriceTest();
			}

			if (window.bundleBuilder.tieredDiscount) {
				bundleProps['_TieredDiscount'] = true;
				bundleProps['_BundleDiscountTier'] = Math.round(this.discount * 100);
			}

			cartItems.push({
				id: variant.id.replace('gid://shopify/ProductVariant/', ''),
				quantity: 1,
				properties: {
					_ItemPrice: this.discount > 0 ? discountedCents - extraDiscountCents: originalCents - extraDiscountCents,
					_BundleDiscount: originalCents - discountedCents,
					_bundleIndex: idx, // Prevents Shopify merging same variants when adding to cart
					...bundleProps,
					...preorderProps
				}
			});
		}
		
		cartItems.reverse();

		if (this.lineItem) {
			window.preventCartUpdate = true;

			trackEvent('BYOB Edited Bundle', {
				location: this instanceof BundleSummary ? 'Summary Step' : 'Summary Bar',
				tier: window.bundleBuilder.tieredDiscount ? Math.round(this.discount * 100) : null
			});

			await fetch(`/cart/change.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({id: this.lineItem, quantity: 0})
			});

			await fetch('/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({items: cartItems})
			});

			const params = new URLSearchParams(window.location.search);
			if (params.has('return_to')) window.location.href = params.get('return_to');
			else window.location.href = '/cart';
		}
		else {
			await fetch('/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({items: cartItems})
			});

			if ((window.featureEnabled && window.featureEnabled('show-cart')) || Shopify.theme.name.includes('GWP') || window.innerWidth > 992) {
				await window.updateCartDrawer();
				window.showCartDrawer(true);
			} else {
				window.updateCartDrawer();
				window.showCartDrawer();
			}

			this.shadowRoot.querySelector('button[name="atc"]').classList.remove('loading');
			this.dispatchEvent(new CustomEvent('addtocart', { bubbles: true, composed: true }));
			document.body.classList.add('bundle-added');

			trackEvent('BYOB Add To Cart', {
				location: this instanceof BundleSummary ? 'Summary Step' : 'Summary Bar',
				tier: window.bundleBuilder.tieredDiscount ? Math.round(this.discount * 100) : null,
				items: variants.length
			});

			fireEvent('Finished', variants.length);
		}
	}

	openCompetitionModal() {
		const wishlistButton = document.querySelector('wishlist-add-button-byob button:not(.added)');
		wishlistButton?.click();

		const modal = document.querySelector('bundle-competition');

		modal.items = this.required.map(item => ({
			sku: item.variant.sku,
			title: item.variant.product.title,
			image: item.variant.product.featuredImage.url + '&width=800',
			url: `${window.location.origin}/products/${item.variant.product.handle}`,
			options: item.variant.options
		}));

		modal.open();
	}
}

class BundleSummary extends BundleSummaryBase {
	static get properties() {
		return {
			...BundleSummaryBase.properties,
			active: { type: Boolean },
			removed: { type: Array },
			legacySubtotals: { type: Boolean, reflect: true }
		}
	}

	constructor() {
		super();

		this.active = false;
		this.bundleImage = null;
		this.removed = [];
		this.legacySubtotals = theme.storeLocation == 'US';
	}

	updated() {
		if (this.valid) {
			const slotItems = [...this.shadowRoot.querySelectorAll('slot')].map(slot => slot.assignedElements({flatten: true})).flat();

			for (const item of slotItems) {
				// Update discount pill
				const discountPill = item.querySelector('discount-pill');
				if (discountPill) discountPill.dataset.price = Math.round(this.totalCents / 100);

				// Update loyalty points widget
        const loyaltyWidget = item.querySelector('yotpo-points-widget');
        if (loyaltyWidget) {
          loyaltyWidget.dataset.price = Math.round(this.totalCentsDisplay / 100);
        }

        // Update loyalty points pill
        const pointsPill = item.querySelector('points-pill');
        if (pointsPill) {
          pointsPill.dataset.price = this.totalCentsDisplay.toFixed(0);
        }
			}

				const wishlistButton = this.querySelector('wishlist-add-button-byob');
				if (wishlistButton) {
					if (window.cottonComingSoon && this.required.some(v => v.opt)) wishlistButton.style.display = 'none';
					else wishlistButton.style.display = '';
				}
		}
	}

	toggleBundleItems() {
		if (this.active) fireEvent('Hide-Bundle-Items');
		else fireEvent('Show-Bundle-Items');

		this.active = !this.active;
	}

	render() {
		const taxType = 
			theme.storeLocation == 'AU' ? 'GST' : 
			theme.storeLocation == 'UK' ? 'VAT' :
			null;

		if (!this.valid) return html`
			<button 
				name="finish" 
				@click=${() => this.dispatchEvent(new CustomEvent('tabchange', { detail: 'Extras', bubbles: true, composed: true }))}
			>
				Finish Choosing Your Extras
			</button>`;
		const length = this.required.length + this.extras.length;

		const items = html`
			${this.hasPreorder && this.preorderShippingText.length > 0 ?
				html`<p class="preorder-shipping">
					${this.preorderShippingText}
				</p>` : ''
			}
			${this.required.map((item, idx) => html`
				<bundle-summary-item role="listitem" .variant=${item.variant} .tab=${item.tab} .itemId=${item.id} 
					step=${idx + 1}
					originalCents=${this.getVariantOriginalCents(item.variant)} 
					discountedCents=${this.getVariantDiscountedCents(item.variant)} 
					extraDiscountCents=${this.getVariantAutomaticDiscountCents(item.variant)}
					currencySymbol=${this.currencySymbol}
					?enableRemove=${window.bundleBuilder.tieredDiscount && idx > 1 && idx == this.required.length - 1}
					@variantremove=${() => this.removed = [item, ...this.removed]}
					>
				</bundle-summary-item>
			`)}
			${this.removed.map((item, idx) => html`
				<bundle-summary-item role="listitem" .variant=${item.variant} .tab=${item.tab} .itemId=${item.id} 
					step=${this.required.length + idx + 1}
					originalCents=${this.getVariantOriginalCents(item.variant)} 
					discountedCents=${this.getVariantDiscountedCents(item.variant)} 
					extraDiscountCents=${this.getVariantAutomaticDiscountCents(item.variant)}
					currencySymbol=${this.currencySymbol}
					?enableUndo=${idx == 0}
					?removed=${true}
					@variantadd=${() => this.removed = this.removed.filter(i => i != item)}
				></bundle-summary-item>
			`)}
			${this.extras.length > 0 ? html`<span class="separator"></span>` : ''}
			${this.extras.map(item => html`
				<bundle-summary-item role="listitem" .variant=${item.variant} .tab=${item.tab} .itemId=${item.id} .enableRemove=${true} 
					originalCents=${this.getVariantOriginalCents(item.variant)} 
					discountedCents=${this.getVariantDiscountedCents(item.variant)} 
					extraDiscountCents=${this.getVariantAutomaticDiscountCents(item.variant)}
					currencySymbol=${this.currencySymbol}
					></bundle-summary-item>
			`) }
		`;

		return html`
			<div class="header">
				<h2>Your Bundle</h2>
				<button class="button button--tertiary see-items" @click=${() => this.shadowRoot.getElementById('items-modal').open()}
					data-fs-element="BYOB See Items"
				>
					See Items (${length})
				</button>
				<slide-out-modal id="items-modal" @modalclose=${() => this.removed = []} z-index="8000">
					<h3 class="items-header">Items included in your bundle</h3>
					<ul class="items--popup">
						${items}
					</ul>
				</slide-out-modal>
			</div>

			<div class="footer">
				<table>
					<tr class="subtotal">
						<th>Subtotal</th>
						<td>${this.formatMoney(this.subTotalCents)}</td>
					</tr>
					${this.bundleDiscountCents > 0 ? html`
						<tr class="bundle-discount">
							<th>
								${!this.legacySubtotals && this.saleDiscountCents == 0 ? html`
									Discount (${(this.discount * 100).toFixed(0)}% off)
								` : html`
									${this.legacySubtotals ? 'Bundle' : ''} Discount <span class="discount-amount">${(this.discount * 100).toFixed(0)}% off</span>
								`}
							</th>
							<td>-${this.formatMoney(this.bundleDiscountCents)}</td>
						</tr>
					` : ''}
					${this.saleDiscountCents > 0 ? html`
					<tr class="sale-discount">
						<th>
							Sale Discount
							<span class="discount-amount">+${Math.floor((this.saleDiscountCents / this.totalCents) * 100)}% off</span>
						</th>
						<td>-${this.formatMoney(this.saleDiscountCents)}</td>
					</tr>
					`: ''}
					<tr class="shipping">
						<th>Shipping</th>
						<td>Free</td>
					</tr>
					<tr class="total">
						<th>Total ${taxType ? `(incl. ${taxType})`: ''}</th>
						<td>
							<span class="total-price">
								${this.subTotalCents != this.totalCentsDisplay ? html`<s>${this.formatMoney(this.subTotalCents)}</s>` : ''}
								${this.formatMoney(this.totalCentsDisplay)}
							</span>
						</td>
					</tr>
				</table>
				<slot name="promo-pill"></slot>
				${window.cottonComingSoon && this.required.some(r => r.variant.options['Fabric'] === 'Cotton') ? html`
				<button class="button button--primary" name="atc" ?disabled=${!this.valid}
					@click=${() => this.openCompetitionModal()}
				>
					Add to Wishlist to WIN
				</button>
				` : html`
				<button name="atc" class="button button--primary" @click=${this.addToCart}>${this.lineItem ? 'Save' : 'Add to Cart'}</button>
				`}
			</div>
			<slot name="footer-items"></slot>
		`;
	}

	static get styles() {
		return css`
			${buttonStyles}

			:host {
				display: block;
				padding-inline: 8px;
			}

			:host([hide-trailing-zeroes]) .zeroes {
				font-size: 0px;
  			line-height: 0;
			}

			button[name="finish"] {
				font: inherit;
				display: block;
				background: var(--color-bg-inverted-primary);
				border: none;
				color: white;
				border-radius: 2px;
				padding: 7px 16px;
				margin: 8px auto;
				cursor: pointer;
			}

			.header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 0;
				padding-block: 8px;
			}

			h2 {
				font-weight: 400;
				font-size: 20px;
				margin: 0;
			}

			.see-items {
				font-size: 14px;
			}

			.items-header {
				font-size: 20px;
				margin: 0;
				font-weight: 400;
				padding: var(--padding-padding-x-small, 8px) 0px var(--padding-padding-small, 16px) 0px;
				border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
				text-align: center;
			}

			.items--popup {
				padding: 0;
				margin: 0;
				padding-bottom: 120px;
				padding-top: 8px;
				gap: 8px;
				display: flex;
				flex-direction: column;
				min-width: 310px;
			}

			button[name="toggle"] {
				font: inherit;
				color: inherit;
				border: none;
				background: none;
				font-size: 14px;
				display: flex;
				gap: 4px;
				align-items: center;
				padding: 8px;
				margin: -8px;
				cursor: pointer;
			}

			button[name="toggle"]::after {
				display: block;
				content: '';
				width: 19px;
				height: 18px;
				background-image: url("data:image/svg+xml,%3Csvg width='19' height='18' viewBox='0 0 19 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.65625 7L9.65625 11L13.6563 7' stroke='%23212121' stroke-width='1.5'%3E%3C/path%3E%3C/svg%3E");
				transition: transform 150ms;
			}

			button[name="toggle"][data-expanded="true"]::after {
				transform: scaleY(-1);
			}

			.items {
				max-height: 0;
				padding: 0;
				margin: 0;
				overflow: hidden;
				list-style-type: none;
				transition: max-height calc(var(--items) * 80ms), padding-bottom 250ms;
				display: grid;
				gap: 16px;
				border-bottom: 1px solid #CACACA;
			}

			.items[data-expanded="true"] {
				max-height: var(--height);
				padding-bottom: 16px;
			}

			.preorder-shipping {
				margin: 0;
				padding: 8px;
				font-size: 14px;
				font-weight: 500;
				line-height: normal; 
				background: var(--surface-surface-secondary, #F4F4F4); 
			}

			.separator {
				display: block;
				border-top: 1px solid #CACACA;
			}

			.footer {
				margin-bottom: 8px;
				padding-top: 0;
				border-top: 1px solid var(--color-border-border-primary, #E1E1E1);
			}

			::slotted([slot="summary-footer-items"]) {
				padding-inline: 0 !important;
			}

			table {
				width: 100%;
				vertical-align: middle;
				border-spacing: 0;
				line-height: 19px;
				margin-bottom: 0;
			}

			table tbody {
				display: flex;
				flex-direction: column;
			}

			tr {
				display: flex;
			}

			th {
				text-align: left;
				font-weight: 400;
			}

			td {
				text-align: right;
			}

			:is(th, td) {
				flex: 1;
				font: var(--text-md);
				padding-block: 8px;
			}

			:host([legacysubtotals]) :is(th, td) {
				padding-block: 4px;
			}

			.subtotal {
				order: -2;
			}

			:host(:not([legacysubtotals])) .shipping {
				order: -1;
			}

			.discount-amount {
				background: var(--color-bg-tertiary, #EBEBEB);
				margin-left: 4px;
				border-radius: 100px;
				color: var(--color-text-primary, #212121);
				font-size: 14px;
				line-height: 20px;
				padding: 4px 14px;
			}

			.shipping td {
				display: flex;
				align-items: center;
				justify-content: right;
				gap: 6px;
			}

			:host([popup][legacysubtotals]) .shipping td::before {
				content: '';
				width: 16px;
				height: 16px;
				display: block;
				background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.00016 14.6673C11.6821 14.6673 14.6668 11.6825 14.6668 8.00065C14.6668 4.31875 11.6821 1.33398 8.00016 1.33398C4.31826 1.33398 1.3335 4.31875 1.3335 8.00065C1.3335 11.6825 4.31826 14.6673 8.00016 14.6673Z' stroke='%23212121' stroke-width='0.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M4.6665 8.33398L6.6665 10.334L11.3332 5.66732' stroke='%23212121' stroke-width='0.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
			}

			.total td, .total th {
				padding-top: 8px;
				font-weight: 400;
				font: var(--text-lg);
				padding-block: var(--padding-sm);
				border-top: 1px solid var(--color-border-border-primary, #E1E1E1);
			}

			.total-price {
				font-size: 20px;
				line-height: normal;
			}

			.total-price s {
				font-size: 14px;
				line-height: 20px;
				color: var(--color-text-disabled, #949494);
			}

			.sale-discount {
				color: var(--color-text-sale); 
			}

			button[name="atc"] {
				width: 100%;
				font: var(--text-md);
			}

			button[name="atc"].loading {
				color: transparent !important;
				pointer-events: none;
				position: relative;
			}

			button[name="atc"].loading::after {
				content: '';
				position: absolute;
				height: 16px;
				width: 16px;
				left: calc(50% - 8px);
				top: calc(50% - 8px);
				opacity: 60%;
				animation: show-loading-anim 1s linear infinite;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z'/%3E%3C/svg%3E");
				filter: invert(1);
			}

			@keyframes show-loading-anim {
				0% {
						transform: rotate(0deg);
				}
				100% {
						transform: rotate(360deg);
				}
			}

			@media screen and (min-width: 992px) {
				button[name="atc"] {
					font: var(--text-lg);
				}
			}
		`;
	}
}

customElements.define('bundle-summary', BundleSummary);


class BundleSummaryItem extends LitElement {
	static get properties() {
		return {
			variant: { type: Object },
			tab: { type: String },
			itemId: { type: String },
			enableRemove: { type: Boolean, reflect: true },
			enableUndo: { type: Boolean, reflect: true },
			originalCents: { type: Number },
			discountedCents: { type: Number },
			extraDiscountCents: { type: Number },
			currencySymbol: { type: String },
			step: { type: Number },
			removed: { type: Boolean, reflect: true }
		}
	}

	constructor() {
		super();
		this.variant = null
		this.tab = '';
		this.itemId = null;
		this.enableRemove = false;
		this.extraDiscountCents = 0;
		this.step = null;
		this.removed = false;

		window.onFeatureEnabled('hide-trailing-zeroes', () => this.setAttribute('hide-trailing-zeroes', ''));
	}

	formatMoney(cents) {
		const money = (cents / 100).toFixed(2);
		return cents == 0 ? 'FREE' : html`${this.currencySymbol}${money.includes('.00') ? html`${money.replace('.00', '')}<span class="zeroes">.00</span>` : money}`;
	}

	editItem() {
		this.dispatchEvent(new CustomEvent('tabchange', { detail: this.tab, bubbles: true, composed: true }));
		this.dispatchEvent(new CustomEvent('bundle:stepchange', { detail: { step: this.step }, bubbles: true, composed: true }));
		trackEvent('BYOB Edit Item', { step: this.tab });
		fireEvent('Edit', null, `Edit-${this.variant.product.productType}`);
	}

	removeItem() {
		this.dispatchEvent(new CustomEvent('variantremove', { detail: this.itemId, bubbles: true, composed: true }));
		trackEvent('BYOB Remove Item', { step: this.tab });
		fireEvent('Remove', null, `Remove-${this.variant.product.productType}`);
	}

	undoItem() {
		this.dispatchEvent(new CustomEvent('variantadd', { detail: { id: this.itemId, variant: this.variant }, bubbles: true, composed: true }));
	}

	render() {
		if (!this.variant) return html``;
		const type = this.variant.product.productType;
		let title = type;
		
		if (type.includes('Pillowcase')) {
			title = this.tab.includes('First') ? 'Pillowcase Set' : '2nd Pillowcase Set';
		}
			
		return html`
			<h3>${`${this.step ? `${this.step}.` : ''} ${title}`}</h3>
			<div class="details">
				<bundle-variant-selections .variant=${this.variant}></bundle-variant-selections>
				${this.variant.currentlyNotInStock ? html`<span class="note">Pre-order: ${this.variant.preOrderMonth} delivery</span>` : ''}
			</div>
			<span class="price">
					${this.discountedCents < this.originalCents ? html`<s>${this.formatMoney(this.originalCents)}</s>`: ''}
					${this.extraDiscountCents > 0 ? html`<s>${this.formatMoney(this.discountedCents)}</s>` : ''}
					<span>${this.formatMoney(this.discountedCents - this.extraDiscountCents)}</span>
			</span>
			<button 
				name="edit" 
				@click=${this.editItem}
			>
				Edit
			</button>
			${this.enableRemove ? html`
				<button name="remove" @click=${this.removeItem}>Remove</button>
			`: ''}
			${this.enableUndo ? html`
				<button name="undo" @click=${this.undoItem}>Undo</button>
			`: ''}
		`
	}

	static get styles() {
		return css`
			:host {
				display: grid;
				row-gap: 8px;
				column-gap: 4px;
				grid-template-areas: 
				"title price"
				"details edit";
				grid-template-columns: 1fr min-content;
				padding-block: var(--padding-xs);
				align-items: center;
			}

			:host(:is([enableremove], [enableundo])) {
				grid-template-areas: 
				"title price price"
				"details edit remove";
				grid-template-columns: 1fr min-content min-content
			}

			:host([hide-trailing-zeroes]) .zeroes {
				font-size: 0px;
  			line-height: 0;
			}

			:host([removed]) > :not(button) {
				opacity: 0.3;
			}

			h3 {
				font: var(--text-md);
				color: var(--color-text-primary);
				margin: 0;
				grid-area: title;
			}

			.details {
				list-style-type: none;
				margin: 0;
				padding: 0;
				display: flex;
				flex-direction: column;
				gap: 8px;
				grid-area: details;
				padding-left: var(--padding-sm);
			}

			bundle-variant-selections {
				color: var(--color-text-tertiary, #575757);
			}

			.option {
				font-size: 14px;
				color: rgba(0, 0, 0, 0.6);
			}

			.price {
				grid-area: price;
				white-space: nowrap;
				display: flex;
				gap: 8px;
				font-size: 12px;
				line-height: 16px;
			}

			:host([removed]) .price {
				visibility: hidden;
			}

			.price s {
				color: var(--color-text-disabled, #949494);
			}

			.price s + s + span {
				color: var(--color-text-sale);
			}

			button {
				font: inherit;
				background: none;
				border: none;
				text-decoration: underline;
				padding: 8px;
				margin: -8px;
				cursor: pointer;
				justify-self: end;
				font-size: 12px;
				color: var(--color-text-tertiary, #575757);
			}

			button[name="edit"] {
				grid-area: edit;
			}

			button[name="remove"],
			button[name="undo"] {
				grid-area: remove;
				align-self: end;
			}

			.note {
				display: block;
				font-size: 12px;
				opacity: 0.9;
			}

			:host([removed]) button[name="edit"] {
				display: none !important;
			}
		`;
	}
}

customElements.define('bundle-summary-item', BundleSummaryItem);


class BundleSummaryBar extends BundleSummaryBase {
	static properties = {
		...BundleSummaryBase.properties,
		tiers: { type: Array },
		highlightAtc: { type: Boolean, reflect: true },
		enableAtc: { type: Boolean, reflect: true },
		unlockedAnimation: { type: Boolean },
		currentStep: { type: String },
		forceUpdate: { type: Boolean },
		text: { type: String },
		stepsComplete: { type: Number },
		stepsTotal: { type: Number },
		buttonAction: { type: Function },
		buttonLabel: { type: String },
		buttonStyle: { type: String },
		tiers: { type: Array }
	}

	constructor() {
		super();

		this.tiers = [];
		this.highlightAtc = false;

		this.tiersUnique = [];
		this.unlocked = [];
		this.unlockedCount = 0;
		this.unlockedAnimation = false;
		this.animationHandle = null;
		this.currentStep = null;
		this.forceUpdate = false;
		this.text = '';

		this.stepsComplete = 0;
		this.stepsTotal = 0;

		this.buttonAction = null;
		this.buttonLabel = '';
		this.buttonStyle = 'primary';
		this.buttonDisabled = false;

		this.enableAtc = window.theme.storeLocation != 'US';
		
		this.unlockedEventsFired = new Set();

		window.addEventListener('bundle:restart', () => {
			this.unlockedEventsFired.clear();
		});

		window.addEventListener('bundle:nextaction', e => {
			if (this.tiers.length == 0) {
				this.buttonAction = e.detail.action;
				this.buttonLabel = e.detail.label;
				this.buttonStyle = e.detail.style ?? 'primary';
				this.buttonDisabled = e.detail.disabled ?? false;
			}
		});
	}

	willUpdate(changedProperties) {
		super.willUpdate();

		if (changedProperties.has('tiers')) this.classList.toggle('no-tiers', this.tiers.length == 0);

		// Only update tiers when step changes, or when discount
		if (!this.currentStep || changedProperties.has('currentStep') || this.forceUpdate) {
			if (this.tiers.length > 0) {
				this.tiersUnique = this.tiers.filter((t, i) => t > 0 && this.tiers.indexOf(t) === i);
				this.unlocked = this.tiersUnique.filter(tier => this.discount * 100 >= tier);

				if (this.unlocked.length > this.unlockedCount) {
					if (this.animationHandle) {
						clearTimeout(this.animationHandle);
						this.unlockedAnimation = false;
						this.animationHandle = null;
					}

					for (const tier of this.unlocked) {
						if (!this.unlockedEventsFired.has(tier)) {
							trackEvent('BYOB Tier Unlocked', { tier });
							this.unlockedEventsFired.add(tier);
						}
					}

					this.animationHandle = setTimeout(() => {
						this.unlockedAnimation = true;
						
						this.animationHandle = setTimeout(() => {
							this.unlockedAnimation = false;
							this.animationHandle = null;
						}, 2000);
					}, 500);
				}

				this.unlockedCount = this.unlocked.length;
			}
		}

		if (this.tiers.length == 0 && changedProperties.has('stepsComplete')  && this.stepsComplete == this.stepsTotal && !this.unlockedAnimation) {
			setTimeout(() => {
				this.unlockedAnimation = true;
				setTimeout(() => {
					this.unlockedAnimation = false;
					this.animationHandle = null;
				}, 2000);
			}, 500);
		}
	}

	render() {
		const progressPerc = this.tiers.length > 0 ? 
			(this.unlocked.length / this.tiersUnique.length) * 100 :
			(this.stepsComplete / this.stepsTotal) * 100;

		const originalCents = this.formatMoney(this.subTotalCents);
		const discountedCents = this.formatMoney(this.totalCentsDisplay);

		if (progressPerc == 100 && !this.highlightAtc) {
			setTimeout(() => this.highlightAtc = true, 600);
		}
		else if (progressPerc < 100) this.highlightAtc = false;

		const barProgress = progressPerc < 100 ? progressPerc * 0.9 : progressPerc;

		return html`
			<div class="progress ${this.tiers.length == 0 ? 'no-tiers' : ''}" @click=${() => document.getElementById('bundle-tier-modal').open()} data-fs-element="BYOB How It Works Progress Bar">
				<div class="bar ${this.tiers.length > 0 && this.unlockedCount == 0 ? 'bar--empty': ''} ${this.unlockedAnimation ? 'bar--unlocked' : ''}" style="--progress: ${barProgress}%"></div>
				${this.tiers.length > 0 ? html`
					<ul class="tiers">
						${this.tiersUnique.map(tier => html`<li class="tier" ?disabled=${this.unlocked.indexOf(tier) != this.unlocked.length - 1}><span>${tier}% Off</span></li>`)} 
					</ul>
				` : html`<li class="tier" ?disabled=${this.stepsComplete < this.stepsTotal}><span>20% Off</span></li>`
				}
				${this.text ? html`<p class="text">${this.text}</p>` : ''}
			</div>
			<div class="summary">
				${this.enableAtc ? html`
					<div class="price">
						<div class="amount">
							${originalCents != discountedCents ? html`<s>${this.formatMoney(this.subTotalCents)}</s>` : ''}
							${this.saleDiscountCents > 0 ? html`<s>${this.formatMoney(this.totalCentsDisplay + this.saleDiscountCents)}</s>` : ''}
							<span>${this.formatMoney(this.totalCentsDisplay)}</span>
						</div>
						${originalCents != discountedCents && this.saleDiscountCents == 0 ? html`
							<div class="savings">Save ${this.formatMoney(this.subTotalCents - this.totalCentsDisplay)}</div>
						` : ''}
					</div>
				`: ''}

				${window.cottonComingSoon && this.required.some(r => r.variant.options['Fabric'] === 'Cotton') ? html`
						<button class="button button--primary expand" name="atc" ?disabled=${!this.valid} style="width: auto;"
							@click=${() => this.openCompetitionModal()}
						>
							Add to Wishlist to WIN
						</button>
				`: this.buttonAction != null && !this.valid ? html`
					<button
						class="button button--${this.buttonStyle} ${this.saleDiscountCents > 0 ? 'expand': ''}"
						name="atc"
						?disabled=${this.buttonDisabled}
						@click=${this.buttonAction}
					>
						${this.buttonLabel}
					</button>
				` : this.enableAtc ? html`
					<button 
						class="button button--primary ${this.saleDiscountCents > 0 ? 'expand': ''}" 
						?disabled=${!this.valid}
						name="atc"
						@click=${this.addToCart}
					>
						${this.lineItem ? 'Save' : `Add to Cart (${this.required.length + this.extras.length})`}
					</button>
				`: html`
					<button
						class="button button--primary view-summary ${this.saleDiscountCents > 0 ? 'expand': ''}"
						?disabled=${!this.valid}
						@click=${() => this.dispatchEvent(new CustomEvent('bundle:stepchange', { detail: { name: 'View Summary' }, bubbles: true, composed: true }))}
					>
						View Summary (${this.required.length + this.extras.length})
					</button>
				`}
			
			</div>
		`;
	}

	static styles = css`
		${buttonStyles}

		:host {
			position: fixed;
			left: 0;
			bottom: 0;
			right: 0;
			z-index: 9000;
			background: white;
			padding: var(--padding-sm);
			box-shadow: 0px -8px 24px 0px rgba(0, 0, 0, 0.12);
			transform: translateY(0);
			opacity: 1;
			transition: transform 300ms, opacity 300ms;
			display: flex;
			flex-direction: column;
			gap: 8px;
		}

		:host(.no-tiers:not([enableatc])) {
			flex-direction: row;
			gap: var(--padding-md);
		}

		:host([hidden]) {
			display: flex;
			transform: translateY(100%);
			opacity: 0;
			pointer-events: none;
		}

		:host([hide-trailing-zeroes]) .zeroes {
			font-size: 0px;
  		line-height: 0;
		}

		.text {
			margin: 0;
			color: var(--color-button-text-secondary, #212121);
			font: var(--text-md);
			text-align: center;
			margin-top: 4px;
		}

		.progress {
			min-width: 330px;
			cursor: pointer;
		}

		.progress.no-tiers {
			pointer-events: none;
			display: flex;
			align-items: center;
			gap: var(--padding-xs);
			min-width: 0;
			max-width: 300px;
			flex: 1;
		}

		.bar {
			height: 12px;
			background: var(--color-bg-secondary, #F2F2F2);
			border-radius: 6px;
			position: relative;
			flex: 1;
		}

		.bar::before {
			display: block;
			content: '';
			height: 100%;
			width: 100%;
			border-radius: 6px;
			--clip: calc(100% - var(--progress, 0));
			clip-path: inset(0 var(--clip) 0 0);
			background: #35AF56;
			transition: clip-path 500ms ease-in-out, background-color 300ms;
		}

		.bar--empty::before {
			background: var(--color-bg-inverted-tertiary, #767676);
			--clip: 97%;
		}

		@keyframes unlocked {
			0% {
				transform: scale(0.1);
				filter: drop-shadow(0px 0px 3px yellow) brightness(0.9);
			}
			20% {
				transform: scale(1);
				filter: drop-shadow(0px 0px 3px yellow) brightness(1.3);
				opacity: 1;
			}
			100% {
				transform: scale(0.8);
				filter: drop-shadow(0px 0px 3px yellow) brightness(1);
				opacity: 0;
			}
		}

		.bar--unlocked::after {
			content: '✨';
			display: block;
			font-size: 80px;
			line-height: 80px;
			width: 80px;
			height: 80px;
			position: absolute;
			left: calc(var(--progress) - 40px);
			top: calc(50% - 40px);
			transform-origin: center;
			animation: unlocked 1.5s both;
		}

		.tiers {
			padding: 0;
			margin: 0;
			display: flex;
			list-style: none;
			padding-right: 10%;
		}

		.tier {
			flex: 1;
			text-align: right;
			padding-right: 2px;
			transition: color 300ms;
			color: var(--color-text-primary);
			list-style: none;
		}
		
		.progress.no-tiers .tier {
			flex: initial;
		}

		.tier span {
			font: var(--text-sm);
			display: inline-block;
		}

		.tier[disabled] {
			color: var(--color-text-disabled, #949494);
		}

		.summary {
			display: flex;
			gap: var(--padding-sm);
			white-space: nowrap;
			justify-content: end;
		}

		@keyframes original-anim {
			0% {
				opacity: 0;
			}
			100% {
				opacity: 1;
			}
		}

		.price {
			flex: 1;
			display: flex;
			flex-direction: column;
			align-items: end;
			justify-content: center;
			font: var(--text-md);
			min-width: max-content;
		}

		.price s {
			animation: original-anim 300ms both;
			animation-delay: 600ms;
			color: var(--color-text-disabled);
		}

		.price s + s + span {
			color: var(--color-text-sale);
		}

		@keyframes savings-anim {
			0% {
				max-height: 0;
				opacity: 0;
			}
			100% {
				max-height: 16px;
				opacity: 1;
			}
		}

		.savings {
			display: inline-block;
			font: var(--text-sm);
			overflow: hidden;
			color: var(--color-text-disabled, #949494);
			animation: savings-anim 300ms both;
			animation-delay: 600ms;
		}

		.button {
			flex: 0 1 130px;
			min-width: 120px;
			transition: flex-basis 300ms, color 300ms, background-color 300ms, border-color 300ms;
		}

		:host([highlightatc]) button[name="atc"].button--primary,
		button[name="atc"].expand {
			flex-basis: 220px;
		}

		button[name="atc"] {
			font: var(--text-md);
		}

		button[name="atc"].loading {
			color: transparent !important;
			pointer-events: none;
			position: relative;
		}

		button[name="atc"].loading::after {
			content: '';
			position: absolute;
			height: 16px;
			width: 16px;
			left: calc(50% - 8px);
			top: calc(50% - 8px);
			opacity: 60%;
			animation: show-loading-anim 1s linear infinite;
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z'/%3E%3C/svg%3E");
			filter: invert(1);
		}

		button[name="atc"].button--secondary.loading::after {
			filter: none;
		}

		@keyframes show-loading-anim {
			0% {
					transform: rotate(0deg);
			}
			100% {
					transform: rotate(360deg);
			}
		}

		@media screen and (max-width: 512px) and (max-height: 680px) {
			:host {
				padding-block: 12px;
				gap: 4px;
			}

			.bar {
				height: 10px;
			}
		}

		@media screen and (max-width: 512px) {
			.view-summary {
				width: 100%;
			}
		}
		
		@media screen and (min-width: 992px) {
			:host {
				gap: 40px;
				flex-direction: row;
				justify-content: end;
			}

			.summary {
				align-self: start;
			}

			.button {
				width: 130px;
			}
		}
	`;
}

customElements.define('bundle-summary-bar', BundleSummaryBar);


class BundleStep extends LitElement {
	static properties = {
		name: { type: String },
		active: { type: Boolean, reflect: true },
		step: { type: Number },
		prevSelected: { type: Boolean },
		nextSelected: { type: Boolean },
		selectedVariant: { type: Object },
		disabled: { type: Boolean },
		isNext: { type: Boolean, reflect: true },
		tier: { type: Number },
		underline: { type: Boolean },
		subtitle: { type: String },
		tag: { type: String },
		otherActive: { type: String, reflect: true },
		optionAction: { type: Object },
	}

	constructor() {
		super();

		this.name = '';
		this.step = 0;
		this.active = false;
		this.prevSelected = false;
		this.nextSelected = false;
		this.selectedVariant = null;
		this.disabled = false;
		this.isNext = false;
		this.tier = 0;
		this.otherActive = null;

		this.underline = false;
		this.subtitle = '';
		this.tag = ''

		this.viewed = false;

		this.optionAction = null;

		window.addEventListener('bundle:stepopen', e => {
			if (e.detail.name != this.name) {
				this.otherActive = e.detail.name;

				if (this.active) {
					this.close();
					trackEvent('BYOB Step Closed', { step: this.name, reason: 'Other Step Opened' });
				}
			}
		});

		window.addEventListener('bundle:stepclose', e => {	
			if (e.detail.name == this.otherActive) this.otherActive = null;
		});

		window.addEventListener('bundle:stepchange', e => {
			if (this.name.includes(e.detail.name) || this.step == e.detail.step) this.open();
		});

		window.addEventListener('bundle:restart', () => {
			this.close();
		});

		this.addEventListener('bundle:optionsconfirmed', () => {
			trackEvent('BYOB Step Closed', { step: this.name, reason: 'Confirmed' });
			this.close();
		});

		this.addEventListener('bundle:optionaction', e => this.optionAction = e.detail);
	}

	render() {
		let name = this.name;

		if (name.includes('Pillowcase')) {
			name = name.includes('First') ? (theme.storeLocation != 'US' ? 'Std Pillowcase Set' : 'Pillowcase Set') : '2nd Pillowcase Set';
		}

		return html`
			<div class="header" @click=${this.toggle} @mousedown=${this.recalculateHeight} @mouseout=${this.clearHeight}>
				<h2 style="${this.underline ? 'text-decoration: underline;' : '' }">${this.step > 0 ? this.step + '.': ''} ${name}</h2>
				${this.selectedVariant ? html`
					<bundle-variant-selections .variant=${this.selectedVariant}></bundle-variant-selections>
				`: window.cottonComingSoon && this.active && !this.name.includes('Summary') ? html` 
					<span style="font: var(--text-sm);">Fibre: <span style="color: #212121;">${window.bundleBuilder.autoSelectOptions['Fabric']}</span></span>
				` :''}
				${this.subtitle || this.tag ? html`
				<div class="tag-subtitle">
					${this.subtitle ? html`<span class="subtitle">${this.subtitle}</span>`: ''}
					${this.tag ? html`<span class="tag">${this.tag}</span>`: ''}
				</div>
				`: ''}
				${this.isNext ? html`
					<div class="tier-container">
						<span class="tier button button--${this.tier > 0 && this.step > 2 ? 'secondary' : 'primary'}">${this.tier > 0 && this.step > 1 ? `${this.step > 2 ? 'Continue' : 'Add'} to get ${this.tier}% off` : 'Add'}</span>
					</div>
				`: ''}
				${this.nextSelected && !this.selectedVariant && !this.active ? html`
					<div class="tier-container">
						<span class="tier button button--error">Select size & ${getOptionName('Colour').toLowerCase()}</span>
					</div>
				`: ''}
				<div class="icons">
					${this.tier > 0 && this.selectedVariant && this.step > 2 && !this.nextSelected && !this.active ? html`
						<button class="remove" @click=${this.remove} aria-label="Remove Item from Bundle">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M20 9L18.005 20.3463C17.8369 21.3026 17.0062 22 16.0353 22H7.96474C6.99379 22 6.1631 21.3026 5.99496 20.3463L4 9" stroke="#212121" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M21 6H15.375M3 6H8.625M8.625 6V4C8.625 2.89543 9.52043 2 10.625 2H13.375C14.4796 2 15.375 2.89543 15.375 4V6M8.625 6H15.375" stroke="#212121" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>									
						</button>
					`: ''}
					${!this.disabled ? html`
						<svg class="chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M7.7998 9.90002L11.9998 14.1L16.1998 9.90002" stroke="#212121" stroke-width="0.7"/>
						</svg>
					` : html`
						<svg class="lock" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M10.6667 7.33333H11.4C11.7314 7.33333 12 7.60196 12 7.93333V13.0667C12 13.398 11.7314 13.6667 11.4 13.6667H4.6C4.26863 13.6667 4 13.398 4 13.0667V7.93333C4 7.60196 4.26863 7.33333 4.6 7.33333H5.33333M10.6667 7.33333V5.66667C10.6667 4.77778 10.1333 3 8 3C5.86667 3 5.33333 4.77778 5.33333 5.66667V7.33333M10.6667 7.33333H5.33333" stroke="#BDBDBD" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
					`}
				</div>
			</div>
			<div class="content">
				<slot></slot>
				${this.tier > 0 && this.selectedVariant && this.step > 2 && !this.nextSelected ? html`
				<button class="remove" @click=${this.remove}>
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M20 9L18.005 20.3463C17.8369 21.3026 17.0062 22 16.0353 22H7.96474C6.99379 22 6.1631 21.3026 5.99496 20.3463L4 9" stroke="#212121" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M21 6H15.375M3 6H8.625M8.625 6V4C8.625 2.89543 9.52043 2 10.625 2H13.375C14.4796 2 15.375 2.89543 15.375 4V6M8.625 6H15.375" stroke="#212121" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>				
					Remove Item from Bundle
				</button>
			`: ''}
			</div>
		`
	}

	willUpdate(changedProperties) {
		this.isNext = this.step >= 1 && (this.prevSelected || this.step == 1) && !this.disabled && !this.active && !this.selectedVariant && !this.nextSelected;

		if ((this.isNext && !this.active && !this.otherActive)) {
			this.dispatchEvent(new CustomEvent('bundle:nextaction', { 
				detail: { 
					label: 'Add Next item', 
					action: () => this.open()
				}, 
				bubbles: true, 
				composed: true 
			}));
		}

		if (this.active) {
			this.dispatchEvent(new CustomEvent('bundle:nextaction', { 
				detail: this.selectedVariant ? { 
					label: 'Confirm', 
					action: () => this.close()
				} : this.optionAction, 
				bubbles: true, 
				composed: true 
			}));
		}

		if (changedProperties.has('active') && this.name.includes('Summary')) {
			document.body.classList.toggle('bundle-summary', this.active);
		}

		if (changedProperties.has('selectedVariant') && !this.selectedVariant) {
			this.viewed = false;
		}

		if (changedProperties.has('active')) {
			if (this.active) this.dispatchEvent(new CustomEvent('open'));
			else this.dispatchEvent(new CustomEvent('close'));
		}

		if (this.active && !this.viewed) {
			this.viewed = true;
			this.dispatchEvent(new CustomEvent('viewed'));
			trackEvent('BYOB Step Viewed', { step: this.name });
			if (this.name.includes('Summary')) fireEvent('Go-To-Summary');
		}
	}

	recalculateHeight() {
		const content = this.shadowRoot.querySelector('.content');
		if (!content) return;
		const height = content.scrollHeight;
		content.style.setProperty('--height', `${height}px`);
		this.heightSet = true;
	}

	clearHeight() {
		if (!this.heightSet) return;
		if (this.heightHandle) clearTimeout(this.heightHandle);

		this.heightHandle = setTimeout(() => {
			this.shadowRoot.querySelector('.content').style.removeProperty('--height');
			this.heightHandle = null;
			this.heightSet = false;
		}, 300);
	}

	remove() {
		this.close();
		trackEvent('BYOB Step Closed', { step: this.name, reason: 'Item Removed' });
		this.dispatchEvent(new CustomEvent('remove'));
	}

	open() {
		this.active = true;
		this.clearHeight();

		this.dispatchEvent(new CustomEvent('bundle:stepopen', { detail: { name: this.name }, bubbles: true, composed: true }));
		trackEvent('BYOB Step Opened', { step: this.name });
	}

	toggle(e) {
		if (e?.composedPath().some(el => el.tagName == 'BUTTON')) return; // Don't toggle if clicking on remove button

		if (this.active) {
			this.close();
			trackEvent('BYOB Step Closed', { step: this.name, reason: 'Accordion Click' });
		}
		else this.open();
	}

	close() {
		this.recalculateHeight();
		setTimeout(() => {
			this.active = false;
			this.clearHeight();
		});
		this.dispatchEvent(new CustomEvent('bundle:stepclose', { detail: { name: this.name }, bubbles: true, composed: true }));
	}

	static styles = css`
		${buttonStyles}

		:host {
			display: block;
			transition: box-shadow 250ms, background-color 350ms;
			border-bottom: 1px solid var(--color-border-border-primary, #E1E1E1);
		}

		:host([isnext]:not([active]):not([otheractive])) {
			box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.20);
		}

		:host([disabled]) {
			pointer-events: none;
		}


		:host(:first-of-type) {
			border-top: 1px solid var(--color-border-border-primary, #E1E1E1);
		}

		:host([active]) {
			background: white;
		}

		.header {
			padding: var(--padding-sm);
			padding-block: 12px;
			display: flex;
			align-items: center;
			cursor: pointer;
			scroll-margin-top: calc(var(--header-height) + 72px);
			gap: 8px;
		}


		@media (hover: hover) {
			.header:hover {
				background: var(--button-bg-hover);
			}
		}

		.header:active {
			background: var(--button-bg-active);
		}

		h2 {
			font: var(--text-md);
			min-width: 120px;
			margin: 0;
			padding-right: var(--padding-sm);
		}

		:host([disabled]) h2 {
			color: var(--color-text-inverted-tertiary, #E1E1E1);
			transition: color 250ms;
		}

		:host([disabled]) path {
			stroke: var(--color-text-inverted-tertiary, #E1E1E1);
			transition: stroke 250ms;
		}

		bundle-variant-selections {
			color: var(--color-text-secondary, #434343);
		}

		.subtitle {
			font: var(--text-sm);
			color: var(--color-text-secondary, #434343);
			margin-right: var(--padding-sm);
		}

		.tag {
			font: var(--text-sm);
			background: var(--color-bg-tertiary, #EBEBEB);
			padding: var(--distance-d-4, 4px) var(--padding-padding-small, 16px);
			border-radius: 16px;
		}

		.icons {
			display: flex;
			gap: 8px;
			margin-left: auto;
			align-items: center;
			justify-content: end;
		}

		svg {
			transition: transform 150ms;
			min-width: 16px;
			display: block;
		}

		:host([active]) .chevron {
			transform: scaleY(-1);
		}

		.lock {
			margin-block: 4px;
		}

		.content {
			max-height: 0;
			opacity: 0;
			pointer-events: none;
			overflow: hidden;
			padding-inline: var(--padding-sm);
			transition: max-height 450ms, opacity 450ms, margin-block 450ms;
			margin-block: 0;
		}

		:host([active]) .content {
			max-height: var(--height, auto);
			opacity: 1;
			pointer-events: all;
			margin-block: 8px;
			overflow: visible;
		}

		.remove {
			display: block;
			background: none;
			border: none;
			padding: 0;
			cursor: pointer;
			position: relative;
			z-index: 5;
			border-radius: 2px;
		}

		.remove:hover {
			background: white;
		}

		.header .remove {
			padding: 8px;
			margin-block: -8px;
			margin-inline: -4px;
		}

		.content .remove {
			padding: var(--padding-sm);
			font: var(--text-md);
			color: var(--color-text-tertiary, #575757);
			display: flex;
			gap: 8px;
			margin-inline: auto;
		}

		.content .remove::after {
			display: block;
			content: '';
			width: 16px;
		}

		button[name="next"],
		.confirm {
			display: block;
			font: inherit;
			border: none;
			background: #212121;
			color: white;
			letter-spacing: .025em;
			padding: 7px 15px;
			border-radius: 2px;
			cursor: pointer;
			text-align: center;
			margin-bottom: var(--padding-md);
		}

		.tier {
			display: block;
			width: fit-content;
			padding-block: 4px;
			margin-block: -4px; /* Prevents from causing header height increase */
			min-width: 120px;
			font: var(--text-md);
			transition: opacity 250ms;
			white-space: nowrap;
		}

		:host([otheractive]) .tier:not(.button--error) {
			opacity: 0;
		}
		
		button[name="next"]:hover,
		button.confirm:hover {
			opacity: 0.9;
		}

		button[name="next"]:active,
		.button.confirm:active {
			opacity: 0.8;
		}

		button[name="next"][aria-disabled="true"],
		.confirm[aria-disabled="true"] {
			opacity: 0.6;
			cursor: auto;
			pointer-events: none;
		}


		@media screen and (max-width: 992px) {
			button[name="next"] {
				width: 100%;
			}
		}

		@media screen and (min-width: 992px) {
			.header {
				justify-content: space-between;
				padding-left: 8px;
				padding-block: var(--padding-sm);
			}

			h2 {
				font: var(--text-lg);
			}

			.content {
				padding-inline: 24px;
			}

			:host([active]) .content {
				margin-block: 16px;
			}

			.icons {
				margin-left: 0;
				min-width: 64px;
			}

			bundle-variant-selections,
			.tier-container,
			.tag-subtitle {
				width: 190px;
			}
		}
	`
}

customElements.define('bundle-step', BundleStep);


class BundleVariantSelections extends LitElement {
	static properties = {
		variant: { type: Object }
	}

	constructor() {
		super();
		
		this.variant = null;
	}

	render() {
		if (!this.variant) return html``;
		const options = this.variant.options;

		return html`
			<img src="${getSwatchUrl(options['Colour'])}" width="12" height="12" loading="lazy" @error=${onSwatchError} />
			<div class="options">
				<span class="colour">${options['Colour'].replace('Scalloped', '').trim()}</span>,&nbsp;
				<span class="size">${options['Size'] ? options['Size'] + ' Size' : options['Type'].replace('Pillowcase', '')}</span>,&nbsp;
				<span class="fabric">${options['Fabric'] ? options['Fabric'] : ''}</span>
			</div>
		`
	}

	static styles = css`
		:host {
			display: flex;
			min-width: 0;
			gap: 4px;
			align-items: center;
		}

		.options {
			font-size: 12px;
			line-height: 16px;
			overflow: hidden;
			white-space: nowrap;
			display: flex;
		}

		span {
			overflow: hidden;
		}

		img {
			border-radius: 2px;
		}

		.colour {
			text-overflow: ellipsis;
		}

		.size {
			min-width: fit-content;
		}
	`;
}

customElements.define('bundle-variant-selections', BundleVariantSelections);

const fetchCache = new Map();

class BundleVariantSelector extends LitElement {
	static get properties() {
		return {
			selectedVariant: { type: Object },
			selectedOptions: { type: Object, attribute: false },
			validOptions: { type: Object },
			collections: { type: Array },
			preOrderMonth: { type: String },
			autoSelectOptions: { type: Object },
			sizeGuide: { type: String },
			sizeTooltip: { type: String },
			discountOverride: { type: Number },
			loaded: { type: Boolean },
			flipped: { type: Boolean },
			step: { type: Number },
			variants: { type: Array },
		}
	}

	constructor() {
		super();

		this.selectedOptions = {};
		this.validOptions = new Map();
		this.selectedVariant = null;
		this.preOrderMonth = '';
		this.autoSelectOptions = {};
		this.productName = '';
		this.sizeGuide = null;
		this.sizeTooltip = null;
		this.discountOverride = null;
		this.loaded = false;
		this.flipped = false;
		this.step = 0;

		this.collections = [];
		this.variants = [];

		this.hiddenColours = [];
		this.activeOptions = new Set();
	}

	async connectedCallback() {
		await this.loadData();
		super.connectedCallback();
	}

	async loadData() {
		const query = /* GraphQL */ `
			query collectionQuery($handle: String!) @inContext(country: ${theme.countryCode}) {
				collection(handle: $handle) {
					title
					products(first: 100) {
						nodes {
							title
							productType
							handle
							tags
							options {
								name
								values
							}
							featuredImage {
								url
							}
							preOrderDate: metafield(namespace: "my_fields", key: "preorder_date") {
								value
							}
							variants(first: 20) {
								nodes {
									id
									sku
									price {
										amount
									}
									availableForSale
									currentlyNotInStock
									quantityAvailable
									selectedOptions {
										name
										value
									}
									preOrderDate: metafield(namespace: "custom", key: "preorder_date") {
										value
									}
									priceTestsMetafield: metafield(namespace: "custom", key: "price_tests") {
										value
									}
								}
							}
						}
					}
				}
			}`

		// Query collections and flatten into array of variants
		const collectionVariants = [];
		const collectionPromises = [];

		for (const collection of this.collections) {
			const collectionPromise = (async () => {
				let promise = null;
				
				if (fetchCache.has(collection)) {
					promise = fetchCache.get(collection);
				}
				else {
					promise = fetch('/api/2023-04/graphql.json', {
						method: 'POST',
						headers: {
								'Content-Type': 'application/json',
								'X-Shopify-Storefront-Access-Token': theme.storefrontToken
						},
						body: JSON.stringify({query: query, variables: {handle: collection}})
					}).then(resp => {
						if (!resp.ok) throw new Error(`Could not load collection ${collection}: ${resp.status} ${resp.statusText}`);
						return resp.json()
					});

					fetchCache.set(collection, promise);
				}
		
				const json = await promise;
				if (json.data.collection === null) throw new Error(`Invalid collection ${collection}`);
				return json.data.collection.products.nodes;
			})();

			collectionPromises.push(collectionPromise);
		}

		const results = await Promise.all(collectionPromises);

		for (const products of results) {
			for (const product of products) {
				product.originalType = product.productType;
				if (product.productType.includes('Scalloped')) product.productType = product.productType.replace('Scalloped ','');
				if (product.productType === 'Pillowcase') product.productType = 'Standard Pillowcase';
				for (const variant of product.variants.nodes) variant.product = product;
			}

			collectionVariants.push(products.flatMap(product => product.variants.nodes));
		}

		this.variants = collectionVariants.flat();

		// Set fabric option
		for (const variant of this.variants) {
			const fabric = variant.product.tags.includes('cotton') ? 'Cotton' : 'Linen';

			if (!variant.selectedOptions.some(option => option.name === 'Fabric')) {
				variant.selectedOptions.push({ name: 'Fabric', value: fabric });
			}
		}

		// Filter out hidden colours
		this.hiddenColours = this.hiddenColours.map(colour => colour.trim());

		this.variants = this.variants.filter(variant => {
			const colour = variant.selectedOptions.find(option => option.name === 'Colour');
			if (colour && this.hiddenColours.includes(colour.value)) return false;
			return true;
		})
		
		this.types = [];
		
		for (let variant of this.variants) {
			variant.options = this.getVariantOptions(variant);
			if (!this.types.includes(variant.product.productType)) this.types.push(variant.product.productType);

			// Set pre-order month
			if (variant.preOrderDate) variant.preOrderMonth = new Date(variant.preOrderDate.value).toLocaleString('en-GB', { month: 'long' });
			else if (variant.product.preOrderDate) variant.preOrderMonth = new Date(variant.product.preOrderDate.value).toLocaleString('en-GB', { month: 'long' });
			else variant.preOrderMonth = this.preOrderMonth;

			// Set discount override
			if (this.discountOverride) variant.discountOverride = this.discountOverride;

			// Parse price tests
			if (variant.priceTestsMetafield) {
				variant.priceTests = JSON.parse(variant.priceTestsMetafield.value).map(price => parseFloat(price));
			}
		}

		if (window.cottonComingSoon) this.allVariants = this.variants;

		this.dispatchEvent(new CustomEvent('load'));
		this.loaded = true;
	}

	updateOptions() {
		// Get all options and their values
		let options = {};

		for (let variant of this.variants) {
			for (let name in variant.options) {
				if (!options[name]) options[name] = [];
				if (!options[name].includes(variant.options[name])) options[name].push(variant.options[name]);
			}
		}

		// Turn option object into into an array of entries, to allow us to sort
		this.options = Object.entries(options).filter(([_, values]) => values.length > 1);
		const optionOrder = [ 'Type', 'Size', 'Fabric', 'Colour'];
		this.options.sort(([a], [b]) => optionOrder.indexOf(a) - optionOrder.indexOf(b));
	}

	getVariantOptions(variant) {
		// Filter out size and type options if they only have one possible choice
		let variantOptions = variant.selectedOptions.filter(
			option => !option.name.match(/(Size|Type|Title)/i) || variant.product.options.find(o => o.name == option.name).values.length > 1 
		);

		return {
			...Object.fromEntries(variantOptions.map(option => [option.name, option.value])),
			'Type': variant.product.productType
		}
	}

	getCurrentType() {
		return this.selectedVariant ? this.selectedVariant.product.originalType :
			this.selectedOptions['Type'] ? this.selectedOptions['Type'] : 
			this.types[0];
	}

	openSizeGuide() {
		const modal = document.getElementById(`size-guide-modal-${this.sizeGuide}`);
		const guide = modal.querySelector('size-guide');
		const type = this.getCurrentType();
		
		modal.open();
		fireEvent('Size-Guide', null, window.bundleBuilderTab);
		trackEvent('BYOB Size Guide Opened', { step: this.productName });
		guide.selectType(type);
	}

	render() {
		if (!this.loaded) return html``;

		const optionTemplates = [];
		let prevOptions = [];

		const hasSize = this.options.some(([name]) => name == 'Size');

		for (let [idx, [name, _]] of this.options.entries()) {	
			if (!this.activeOptions.has(name)) continue;
			let validValues = this.validOptions.get(name);

			optionTemplates.push(html`
				<bundle-option-selector
					class="${this.sizeTooltip && this.sizeTooltip.length > 0 && name != 'Size' ? 'fade' : ''}"
					name=${name}
					.values=${validValues}
					.selected=${this.selectedOptions[name]}
					.prevOption=${prevOptions[prevOptions.length - 1]}
					.prevSelected=${prevOptions.length == 0 || prevOptions.every(option => this.selectedOptions[option])}
					.othersSelected=${Object.keys(this.selectedOptions).some(key => key != name)}
					.autoSelect=${this.autoSelectOptions[name]}
					.productName=${this.productName}
					.enableFlipping=${this.enableFlipping}
					.flipped=${this.flipped}
					.selectedType="${this.getCurrentType()}"
					.step=${this.step}
					?showSizeGuide=${this.sizeGuide && (name == 'Size' || (!hasSize && idx == 0))}
					?dropdown=${name == 'Size' || name == 'Fabric'}
					?last=${idx == this.options.length - 1}
					@optionchange=${e => this.selectOption(name, e.detail)}
					@opensizeguide=${this.openSizeGuide}
					tooltip=${this.sizeTooltip && this.sizeTooltip.length > 0 && name == 'Size' ? this.sizeTooltip : null}
					?hidden=${this.selectedOptions[name] && validValues.length == 1}
				>
					${name == 'Type' ? html`<slot name="type-info" slot="type-info"></slot>` : ''}
				</bundle-option-selector>
			`);

			prevOptions.push(name)
		}

		return html`
			${optionTemplates}
		`;
	}

	static get styles() {
		return css`
			:host {
				display: grid;
				grid-template-columns: 160px 1fr;
				row-gap: 16px;
				column-gap: 8px;
				color: var(--color-text-primary);
				max-width: 500px;
				position: relative;
				max-width: unset;
			}
			
			button[name="size-guide"] {
				font: inherit;
				font-size: 14px;
				font-weight: 400;
				line-height: 16px;
				text-decoration: underline; 
				display: flex;
				align-items: center;
				background: none;
				border: none;
				padding: 4px 0;
				cursor: pointer;
				color: var(--color-text-primary);
			}

			bundle-option-selector {
				transition: opacity 200ms;
			}

			bundle-option-selector.fade {
				opacity: 0.1;
				pointer-events: none;
			}

			bundle-option-selector[name="Size"] {
				order: -2;
			}

			bundle-option-selector[name="Fabric"] {
				order: -1;
			}

			@media screen and (min-width: 992px) {
				:host {
					grid-template-columns: 180px 1fr;
				}
			}
		`;
	}

	validateOptions() {
		// Replace any handlised values with actual values, if applicable
		for (let [optionName, optionValues] of this.options) {	
			for (const value of optionValues) {
				if (handlise(value) === this.selectedOptions[optionName]) {
					this.selectedOptions[optionName] = value;
					break;
				}
			}
		}

		this.activeOptions.clear();

		const selectedEntries = Object.entries(this.selectedOptions);

		for (let [optionName, optionValues] of this.options) {	
			// Filter out variants that don't match the currently selected options
			// Allow variants where the colour doesn't match, as we'll deselect the colour if it doesn't match
			const validVariants = new Set(this.variants.filter(variant => 
				variant.options[optionName] && selectedEntries.every(([name, value]) => name == 'Colour' || name == optionName || variant.options[name] == value)
			));

			if (validVariants.size > 0) this.activeOptions.add(optionName);

			let validValues = new Map();

			// Add the variant's value for this option to the list of valid values
			for (let variant of validVariants) {
				// If the value has already been added, set the existing soldOut and preorder flags to false if the variant is available
				if (validValues.has(variant.options[optionName])) {
					let existing = validValues.get(variant.options[optionName]);
					if (existing.soldOut && !variant.soldOut) existing.soldOut = false;
					if (existing.preorder && !variant.preorder) existing.preorder = false;
					if (existing.lowStock && !variant.lowStock) existing.lowStock = false;
					if (existing.inventory > variant.quantityAvailable) existing.inventory = variant.quantityAvailable;
					if (variant.extraDiscount && existing.extraDiscount && existing.extraDiscount < variant.extraDiscount) existing.extraDiscount = variant.extraDiscount;
				}
				// Otherwise, add the value and the sold out/preorder states
				else validValues.set(variant.options[optionName], { 
					name: variant.options[optionName],
					soldOut: !variant.availableForSale, 
					preorder: variant.currentlyNotInStock,
					lowStock: variant.quantityAvailable <= 5 && variant.options['Fabric'] != 'Cotton',
					inventory: variant.quantityAvailable,
					extraDiscount: variant.extraDiscount
				});
			}

			// For some options, add other variants as potential options
			const extraOptions = ['Type', 'Fabric'];

			if (extraOptions.includes(optionName)) {
				for (const variant of this.variants) {
					const optionValue = variant.options[optionName];
					const existing = validValues.get(optionValue);
					const disabled = selectedEntries.some(([name, value]) => name != optionName && extraOptions.includes(name) && variant.options[name] != value);

					if (optionValue) {
						if (existing) {
							if (existing.disabled && !disabled) existing.disabled = false;
						}
						else validValues.set(optionValue, {
							name: optionValue,
							disabled: disabled,
						});
					}
				}
			}

			const validValuesArray = [...validValues.values()];
			validValuesArray.sort((a, b) => optionValues.indexOf(a.name) - optionValues.indexOf(b.name));
			this.validOptions.set(optionName, validValuesArray);
			
			// If the selected option is no longer valid, try and select dual option that includes it, or otherwise remove it
			if (this.selectedOptions[optionName] && !validValues.has(this.selectedOptions[optionName])) {
				const validMatch = validValuesArray.find(value => value.name.includes('/') && value.name.includes(this.selectedOptions[optionName]));
				if (validMatch) this.selectedOptions[optionName] = validMatch.name;
				else delete this.selectedOptions[optionName];
			}

			// If the selected option is sold out, remove it
			else if (validValues.get(this.selectedOptions[optionName])?.soldOut) delete this.selectedOptions[optionName];
		}

		const currentVariant = this.selectedVariant;

		// If all options are selected, find the variant that matches all options
		if (this.options.every(([name, _]) => name in this.selectedOptions || !this.activeOptions.has(name))) {
			this.selectedVariant = this.variants.find(variant => {
				for (let name in variant.options) {
					if (this.selectedOptions[name] && variant.options[name] != this.selectedOptions[name]) return false;
				}
				return true;
			});
		}
		else this.selectedVariant = null;

		if (this.selectedVariant != currentVariant) this.dispatchEvent(new CustomEvent('variantchange', { detail: this.selectedVariant }));
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('variants')) {
			this.updateOptions();
		}

		if (changedProperties.has('selectedOptions')) {
			if (this.selectedOptions == undefined) this.selectedOptions = {};
			this.validateOptions();

			if (this.selectedOptions['Type'] == 'Quilt' || this.selectedOptions['Type'] == 'Muslin Throw') this.enableFlipping = true;
			else this.enableFlipping = false;

			if (window.cottonComingSoon && (this.autoSelectOptions['Fabric'] || this.selectedOptions['Fabric'])) {
				this.variants = this.allVariants.filter(variant => variant.options['Fabric'] == this.autoSelectOptions['Fabric'] || variant.options['Fabric'] == this.selectedOptions['Fabric']);
			}
		}
	}

	selectOption(name, value) {
		this.selectedOptions[name] = value;
		this.selectedOptions = { ...this.selectedOptions };
		this.dispatchEvent(new CustomEvent('optionchange', { detail: this.selectedOptions }));
	}
}

customElements.define('bundle-variant-selector', BundleVariantSelector);


class BundleOptionSelector extends LitElement {
	static previousSelections = new Map();

	static get properties() {
		return {
			name: { type: String },
			values: { type: Array },
			selected: { type: String },
			prevOption: { attribute: false },
			prevSelected: { type: Boolean, reflect: true },
			othersSelected: { type: Boolean },
			autoSelect: { type: String },
			autoSelected: { type: Boolean },
			productName: { type: String },
			tooltip: { type: String },
			showTypeInfo: { type: Boolean },
			selectedType: { type: String },
			dropdown: { type: Boolean, reflect: true },
			last: { type: Boolean },
			showSizeGuide: { type: Boolean },
			step: { type: Number },
			open: { type: Boolean, reflect: true },
		};
	}

	constructor() {
		super();

		this.name = '';
		this.selected = null;
		this.prevOption = null;
		this.prevSelected = false;
		this.othersSelected = false;
		this.values = [];
		this.swatches = {};
		this.autoSelect = null
		this.autoSelected = false;
		this.autoSelectedName = null;
		this.hasMixed = false;
		this.flipped = false;
		this.enableFlipping = false;
		this.showTypeInfo = false;
		this.selectedType = null;
		this.dropdown = false;
		this.last = false;
		this.showSizeGuide = false;
		this.step = 0;
		this.open = false;

		this.addEventListener('slotchange', e => {
			if (e.target.assignedElements({flatten: true}).length > 0) {
				this.showTypeInfo = true;
			}
		});

		this.productName = '';
	}

	willUpdate(changedProperties) {
		if (this.selected == null) this.autoSelected = false;

		// Check whether option has both regular sizes (eg, Single) and mixed sizes (eg, Single/Double)
		this.hasMixed = this.values.some(value => value.name.includes('/')) && this.values.some(value => !value.name.includes('/'));

		// Automatically select the first type 
		if (!this.autoSelect && this.name == 'Type') this.autoSelect = this.values[0].name;

		if (this.autoSelect) {
			if (this.prevSelected && !this.selected) {
				const target = this.values.find(value => value.name.trim() == this.autoSelect.trim() || (value.name.includes('/') && value.name.includes(this.autoSelect)));
				
				if (target) {
					this.selectOption(target);
					this.autoSelected = true;
				}
			}
		}
		else this.autoSelected = false;

		// Select a previously selected option if it is still available
		if (this.selectedType && this.productName) {
			const key = `${this.productName}-${this.selectedType}-${this.name}`;

			if (!this.selected) {
				const previous = BundleOptionSelector.previousSelections.get(key);
				const target = this.values.find(value => value.name == previous);
				if (target) this.selectOption(target);
			} 
			else BundleOptionSelector.previousSelections.set(key, this.selected);
		}

		// Get swatch image URLs for available colours
		if (this.name == 'Colour') {
			for (let value of this.values) {
				if (!this.swatches[value.name]) {
					this.swatches[value.name] = getSwatchUrl(value.name);
				}
			}
		}

		if (this.name == 'Colour' && this.enableFlipping) {
			if (this.selected && this.selected.includes('&')) {
				this.showFlipToggle = true;
			}
			else {
				this.showFlipToggle = false;
				if (this.flipped) this.setFlip(false);
			}
		} 

		// Trigger next action prompt in summary bar
		if (this.prevSelected && !this.selected) {
			this.dispatchEvent(new CustomEvent('bundle:optionaction', { 
				detail: { 
					label: `Select a ${getOptionName(this.name)}`, 
					style: !this.dropdown ? 'secondary' : 'primary',
					disabled: !this.dropdown,
					action: () => this.shadowRoot.querySelector('slide-out-modal')?.toggle()
				}, 
				bubbles: true, 
				composed: true 
			}));
		}
	} 

	setFlip(value) {
		if (this.flipped == value) return;
		this.flipped = value;
		this.dispatchEvent(new CustomEvent('flipchange', { detail: value, composed: true, bubbles: true }));
	}

	getLabel(value) {
		if (value.includes('Pillowcase')) return html`${value.replace('Pillowcase', '')} <span class="pillows">Pillows</span>`
		return value;
	}

	getStatus(value) {
		if (value.preorder) return 'preorder';
		if (value.soldOut) return 'sold-out';
		if (value.lowStock) return 'low-stock';
		return 'available';
	}

	getStatusText(value) {
		if (value.preorder) return 'on Pre-order';
		if (value.soldOut) return 'Sold out';
		if (value.lowStock) return `${value.inventory} left`;
		return 'In stock';
	}

	selectOption(value, click = false) {
		this.autoSelected = false;
		this.shadowRoot.querySelector('slide-out-modal')?.close();
		
		if (value != this.selected) {
			this.dispatchEvent(new CustomEvent('optionchange', { detail: value.name }));

			if (click) trackEvent('BYOB Option Click', { 
				step: this.productName, 
				option: this.name, 
				value: value.name,
			});
		}
	}

	sizeGuideButton() {
		return (this.prevSelected || this.selected) && this.showSizeGuide ? html`<button name="size-guide" @click=${() => this.dispatchEvent(new CustomEvent('opensizeguide'))}>Size guide</button>`: ''
	}

	render() {
		const selectedValue = this.values.find(value => value.name == this.selected);
		
		let disabledCount = 0;
		for (const value of this.values) {
			if (value.disabled) disabledCount++;
		}

		const fieldset = html`
			<fieldset class="${handlise(this.name)}">
				<ul data-items=${this.values.length}>
				${this.values.map(value => html`
					<button 
						class="option ${this.selected == value.name ? 'selected' : ''}
						${value.preorder ? 'preorder' : ''}
						${value.soldOut ? 'sold-out' : ''}" 
						@click=${e => { if(!value.soldOut) this.selectOption(value, e.isTrusted); } }
						?disabled=${value.disabled}
					>
						<label>${this.name == 'Colour' && value.soldOut ? 'Coming back soon' : this.getLabel(value.name)}</label>
						${this.name == 'Colour' ? html`<img src="${this.swatches[value.name]}" loading="lazy" width="32" height="32" @error=${onSwatchError} />` : ``}
					</button>
				`)}
				${this.name == 'Type' ? html`
					<div class="type-info" ?hidden=${!this.showTypeInfo}>
						<button class="button button--secondary" @click=${e => {e.target.closest('.type-info').querySelector('#type-info-modal').open(); trackEvent('BYOB Info Modal Opened', { step: this.productName })}} aria-label="More info">
						<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M8.50016 14.6666C12.1821 14.6666 15.1668 11.6818 15.1668 7.99992C15.1668 4.31802 12.1821 1.33325 8.50016 1.33325C4.81826 1.33325 1.8335 4.31802 1.8335 7.99992C1.8335 11.6818 4.81826 14.6666 8.50016 14.6666Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M8.5 7.66675V11.0001" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M8.5 5.00667L8.50667 4.99926" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>								
						</button>
						<slide-out-modal id="type-info-modal" no-padding>
							<slot name="type-info"></slot>
						</slide-out-modal>
					</div>
				` : ''}
				</ul>
			</fieldset>
		`;

		return html`
			<div class="main">
			${!this.dropdown ? html`
				<h3>
					<div class="text-wrapper">
						${getOptionName(this.name)}: 
						${this.name == 'Colour' && this.selected ? html`
							<span class="selection ${this.selected.length > 26 ? 'long': ''}">
								<span class="label">${this.getLabel(this.selected)}</span>
									<button class="button button--secondary colour-info" @click=${() => this.dispatchEvent(new CustomEvent('opencolourinfo', { bubbles: true, composed: true, detail: handlise(this.selected) }))}>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M8.00004 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8.00001C14.6667 4.31811 11.6819 1.33334 8.00004 1.33334C4.31814 1.33334 1.33337 4.31811 1.33337 8.00001C1.33337 11.6819 4.31814 14.6667 8.00004 14.6667Z" stroke="#575757" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
											<path d="M8.00012 7.33334V12" stroke="#575757" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
											<path d="M8.00012 5.00664L8.00679 4.99923" stroke="#575757" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
										</svg>						
										${getOptionName('Colour')} Inspo
									</button>
									${selectedValue.extraDiscount ? html`<span class="discount">(Extra ${Math.round(selectedValue.extraDiscount * 100)}% off)</span>` : ''}
								<span class="status status--${this.getStatus(selectedValue)}">${this.getStatusText(selectedValue)}</span>
							</span>
						` : ''}
					</div>
					${this.sizeGuideButton()}
				</h3>
			` : ''}

				${this.dropdown ? html`
					<div class="dropdown-wrapper">
						<button 
							class="button button--secondary dropdown ${!this.selected ? 'dropdown--empty' : ''}" 
							@click=${() => this.shadowRoot.querySelector('slide-out-modal')?.toggle()}
							?disabled=${this.selected && disabledCount >= this.values.length - 1}
						>
							${this.selected ?	html`
								<span>
									<span style="color: var(--color-text-tertiary, #575757);">${getOptionName(this.name)}:</span> 
									${this.getLabel(this.selected)}
								</span>` : `Select ${getOptionName(this.name)}`}
						</button>
						<slide-out-modal desktop="dropdown" z-index="10100" dropdown-width="max-content" @modalopen=${() => this.open = true} @modalclose=${() => this.open = false}>
							<div class="dropdown-content">
								<h3>Select a ${getOptionName(this.name)} ${this.sizeGuideButton()}</h3>
								${fieldset}
								${this.sizeGuideButton()}
							</div>
						</slide-out-modal>
					</div>
				`: html`${fieldset}`}

				</div>
				
				${this.name == 'Colour' && this.enableFlipping ? html`
				<bundle-checkbox 
					class="flip" 
					label=${this.selectedType ? `Flip my ${this.selectedType.split(' ').pop()}!` : "Flip my Quilt!"}
					?checked=${this.flipped} 
					@change=${e => this.setFlip(e.target.checked)}
					?hidden=${!this.showFlipToggle}
				></bundle-checkbox>
				` : ''}
				${this.dropdown && this.prevSelected && !this.selected ? html`
					<button class="button button--secondary open-dropdown"  @click=${() => this.shadowRoot.querySelector('slide-out-modal')?.open()}>Select a ${getOptionName(this.name).toLowerCase()}</button>
				`: ''}
				${this.prevSelected && this.last ? html`
					<button class="button button--${this.step === 1 && this.selected ? 'primary' : 'secondary'} confirm" ?disabled=${!this.selected} @click=${() => this.dispatchEvent(new CustomEvent('bundle:optionsconfirmed', { bubbles: true, composed: true }))}>
						${this.selected ? 'Confirm' : `Select a ${getOptionName(this.name)}`}
					</button>
				` : ''}
		`;
	}

	static get styles() {
		return css`
			:host {
				position: relative;
				transition: opacity 250ms;
				display: flex;
				flex-direction: column;
				column-gap: var(--padding-xs);
				row-gap: var(--padding-sm);
				min-width: 0;
				grid-column: auto / span 2;
			}

			:host([hidden]) {
				display: none !important;
			}

			:host([dropdown]) {
				display: block;
				grid-column: auto / span 1;
				justify-self: start;
			}

			:host([dropdown]:not([open])) {
				z-index: 10;
			}

			@media screen and (min-width: 992px) {
				:host([dropdown]) {
					grid-template-columns: repeat(2, minmax(auto, 180px));
				}
			}

			:host(:not([prevselected])) {
				pointer-events: none;
				opacity: 0.5;
			}

			${buttonStyles}

			.main {
				width: max-content;
				max-width: 100%;
			}

			h3 {
				font: var(--text-md);
				margin: 0;
				margin-bottom: 6px;
				display: flex;
				gap: 8px;
				align-items: center;
			}

			.text-wrapper {
				display: flex;
				gap: 4px;
			}

			.selection {
				font-weight: 400;
				display: flex;
				flex-wrap: wrap;
				gap: 4px 8px;
				color: var(--color-text-primary, #212121);
			}

			.selection .label {
				color: var(--color-text-tertiary, #575757);
			}

			.discount {
				color: var(--color-text-sale); 
				font-size: 14px;
				line-height: 1.4;
			}

			.status {
				color: var(--color-text-tertiary);
				font-size: 12px;
				font-weight: 400;
				display: flex;
				gap: 4px;
				align-items: center;
				flex: 1 1 100%;
				line-height: 1;
			}

			@media screen and (max-width: 410px), (min-width: 992px) and (max-width: 1200px) {
				.selection.long .label {
					flex: 1 1 100%;
				}	

				.selection.long .status {
					flex: 1;
				}	

				.selection.long .status::before {
					content: '|';
					padding-right: 4px;
				}
			}

			.status--available {
				display: none;
			}


			:host(:not([name="Colour"])) .status {
				display: none;
			}

			.note {
				color: rgba(0, 0, 0, 0.5);
			}

			.dropdown-content h3 {
				font: var(--title-sm);
				margin-bottom: 12px;
				line-height: normal;
			}

			@media screen and (max-width: 992px) {
				:host([dropdown]) .text-wrapper ~ button[name="size-guide"] {
					display: none;
				}
			}

			@media screen and (min-width: 992px) {
				.dropdown-content h3 {
					display: none;
				}

			}

			fieldset {
				border: none;
				margin: 0;
				padding: 0;
				row-gap: 12px;
				column-gap: 24px;	
			}

			fieldset.colour {
				display: flex;
				flex-wrap: wrap;
			}
			
			h4 {
				font-weight: 400;
				margin: 0;
				margin-bottom: 2px;
				font-size: 14px;
			}

			ul {
				list-style: none;
				padding: 0;
				margin: 0;
				display: grid;
				grid-template-columns: repeat(3, minmax(30%, 160px));
				gap: 8px;
			}

			ul[data-items="2"] {
				grid-template-columns: repeat(2, minmax(40%, 160px)) 1fr;
			}

			.dropdown-content ul {
				grid-template-columns: repeat(2, 1fr);
			}

			.colour ul {
				display: flex;
				flex-wrap: wrap;
				gap: 8px;
			}

			button {
				font-family: inherit;
			}

			.option {
				border-radius: var(--button-border-radius);
				border: var(--button-border);
				padding: 7px 12px;
				line-height: 20px; 
				background: none;
				color: var(--color-text-primary);
				cursor: pointer;
				padding: var(--padding-padding-x-small, 8px) var(--padding-padding-small, 16px);
				font: var(--text-md);
			}

			.dropdown-content .option {
				width: 100%;
				text-align: left;
				border: 1px solid var(--color-border-border-primary, #E1E1E1);
			}

			@media screen and (min-width: 992px) {
				ul {
					list-style: none;
					padding: 0;
					margin: 0;
					display: grid;
					grid-template-columns: repeat(3, minmax(30%, 180px));
					gap: 8px;
				}

				ul[data-items="2"] {
					grid-template-columns: repeat(2, minmax(40%, 180px)) 1fr;
				}

				.dropdown-wrapper slide-out-modal {
					max-width: 355px;
				}

				.dropdown-content {
					margin-inline: -8px;
				}

				.dropdown-content ul[data-items="2"] {
						grid-template-columns: 165px;
				}

				.dropdown-content .option { 
					width: 165px;
				}
			}

			.option label {
				cursor: pointer;
				white-space: nowrap;
			}

			.colour .option {
				width: 32px;
				height: 32px;
				position: relative;
				background: none !important;
				border: none;
				padding: 0;
				border-radius: 0px;
			}

			.colour img {
				width: 100%;
				height: auto;
			}

			.colour .option label, .option.select label {
				position: absolute;
				opacity: 0;
				pointer-events: none;
				background: var(--color-bg-inverted-primary);
				color: #fff;
				padding: 4px 8px;
				bottom: calc(100% + 6px); 
				left: 50%;
				transform: translateX(-50%);
				white-space: nowrap;
				font: var(--text-sm);
				border-radius: 2px;
			}

			.colour .option label::before, .option.select label::before {
				content: "";
				position: absolute;
				display: block;
				width: 0px;
				left: 50%;
				bottom: 4px;
				border: 8px solid transparent;
				border-bottom: 0;
				border-top: 8px solid #212121;
				transform: translate(-50%,100%);
			}

			@media (hover: hover) {
				.option:hover {
					background: var(--button-bg-hover);
				}

				.colour .option:hover label, .option.select:hover label {
					opacity: 1;
				}

				.option.select:hover {
					background: none;
				}
			}

			.option:active {
				background: var(--button-bg-active);
			}

			.option.sold-out, .option[disabled], .dropdown[disabled] {
				pointer-events: none;
				opacity: 0.5;
				border: var(--button-border) !important;
				background: none !important;
				cursor: not-allowed;
				color: var(--color-text-primary);
			}

			.colour .option.sold-out {
				pointer-events: all;
				cursor: not-allowed;
				opacity: 1;
			}

			.colour .option.sold-out::after {
				--size: 28px;
				position: absolute;
				content: '';
				display: block;
				right: 1px;
				width: 135%;
				transform: rotate(-45deg);
				transform-origin: top right;
				top: 1px;
				height: 0.7px;
				backdrop-filter: blur(2px) grayscale(1) contrast(10) invert(1);
				opacity: 0.7;
			}

			.option.selected {
				border: 1px solid #212121;
				pointer-events: none;
			}

			.colour .option.selected {
				border: none;
				outline: 1px solid #212121;
				outline-offset: 1px;
			}

			.option.select {
				border: none;
				width: 32px;
				height: 32px;
				justify-self: start;
				position: relative;
				padding: 0;
			}

			.dropdown {
				text-align: left;
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 160px;
				font: var(--text-md);
			}

			.dropdown--empty {
				color: var(--color-text-secondary, #434343) !important;
			}

			.modal-open .dropdown {
				border-color: #212121;
				background: white !important;
			}

			.dropdown::after {
				content: '';
				display: block;
				width: 24px;
				height: 24px;
				background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.7998 9.90002L11.9998 14.1L16.1998 9.90002' stroke='%23212121' stroke-width='0.7'/%3E%3C/svg%3E%0A");
				transition: transform 200ms;
			}

			@media screen and (min-width: 992px) {
				.dropdown {
					width: 180px;
				}
			}

			.modal-open .dropdown::after {
				transform: scaleY(-1);
			}

			.type-info {
				align-self: center;
			}

			.type-info button {
				border: none;
				padding: 4px;
				display: flex;
				align-items: center;
				justify-content: center;
			}

			fieldset[data-autoselected="true"] button:not(.selected, .select) {
				display: none;
			}

			@media screen and (max-width: 992px) {
				label .pillows {
					display: none;
				}
			}

			.colour-info {
				display: flex;
				align-items: center;
				gap: 2px;
				padding: 8px;
				margin: -8px;
				border: none;
				font: var(--text-sm);
				color: var(--color-text-tertiary, #575757) !important;
				display: none !important;
			}

			.colour-info svg {
				display: block;
			}
			
			.flip {
				padding-top: 16px;
				overflow: hidden;
				transition: opacity 200ms, max-height 200ms, padding 200ms;
				max-height: 40px;
			}

			.flip[hidden] {
				opacity: 0;
				pointer-events: none;
				max-height: 0;
				padding: 0;
			}

			.open-dropdown, .confirm {
				width: 160px;
				font: var(--text-md);
				transition: opacity 250ms;
			}

			.open-dropdown {
				width: fit-content;
				background:#212121 !important;
				color: white !important;
				border: none;
				position: absolute;
				top: calc(100% + 8px);
				left: 50%;
				transform: translateX(-50%);
			}

			.open-dropdown::before {
				content: '';
				position: absolute;
				top: -4px;
				left: calc(50% - 4px);
				width: 8px;
				height: 8px;
				background: #212121;
				transform: rotate(45deg);
			}

			button[name="size-guide"] {
				font: inherit;
				font-weight: 400;
				line-height: 16px;
				text-decoration: underline; 
				display: flex;
				align-items: center;
				background: none;
				border: none;
				padding: 4px 0;
				cursor: pointer;
				white-space: nowrap;
				margin-left: auto;
				font-size: 12px;
				color: var(--color-text-tertiary, #575757);
				transition: opacity 250ms;
			}

			.dropdown-content > button[name="size-guide"] {
				display: none;
				margin-inline: auto;
				margin-top: var(--padding-xs);
				margin-bottom: -4px;
			}

			@media screen and (min-width: 992px) {
				.confirm {
					width: 180px;
				}

				.main:has(.dropdown-wrapper.modal-open) + .open-dropdown {
					opacity: 0;
					pointer-events: none;
				}

				:host([dropdown]) h3 > button[name="size-guide"] {
					display: none;
				}

				.dropdown-content > button[name="size-guide"] {
					display: flex;
				}
			}
		`;
	}
}

window.addEventListener('bundle:restart', () => BundleOptionSelector.previousSelections.clear());

customElements.define('bundle-option-selector', BundleOptionSelector);


class BundleCheckbox extends LitElement {
	static get properties() {
		return {
			label: { type: String },
			checked: { type: Boolean }
		}
	}

	constructor() {
		super();

		this.label = '';
		this.checked = false;
	}

	render() {
		return html`
			<input type="checkbox" id="checkbox" .checked=${this.checked} @change=${e => this.onChange(e)}>
			<label for="checkbox">${this.label}</label>
		`;
	}

	onChange(event) {
		this.checked = event.target.checked;
		this.dispatchEvent(new CustomEvent('change', { detail: this.checked }));
	}

	static get styles() {
		return css`
			:host {
				display: flex;
				align-items: center;
			}

			input {
				appearance: none;	
				margin: 0;
				cursor: pointer;
			}

			input::before {
				content: '';
				display: block;
				width: 14px;
				height: 14px;
				border-radius: 2px;
				border: 1px solid #212121;
				background-size: 12px 12px;
			}

			input:hover::before {
				background-color: rgba(0, 0, 0, 0.05);
			}

			input:active::before {
				background-color: rgba(0, 0, 0, 0.1);
			}

			input:checked::before {
				background-color: #212121;
				background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.88892 8.25915L7.05317 10.3332L9.08215 8.38878L11.1111 6.44434' stroke='white' stroke-width='1.5' stroke-linecap='square'/%3E%3C/svg%3E%0A");
				background-size: 18px 18px;
				background-position: center;
				transition: background-size cubic-bezier(0, 1.73, 0.25, 1.7) 150ms;
			}

			label {
				padding-left: 6px;
				cursor: pointer;
				line-height: 1.5;
				user-select: none;
			}
		`;
	}
}

customElements.define('bundle-checkbox', BundleCheckbox);

class BundleOnboarding extends LitElement {
	static get properties() {
		return {
			collectionHandle: { type: String },

			pathTitle: { type: String },
			pathDescription: { type: String },
			styleTitle: { type: String },
			styleDescription: { type: String },
			sizeTitle: { type: String },
			sizeDescription: { type: String },
			sizeDisclaimer: { type: String },
			fromScratchLabel: { type: String },
			fromScratchImage: { type: String },
			sessionTitle: { type: String },
			sessionDescription: { type: String },
			sessionImage: { type: String },

			fabricTitle: { type: String },
			fabricDescription: { type: String },
			fabrics: { type: Array },

			comboLabel: { type: String },
			products: { type: Array },
			panel: { type: String },
			sizes: { type: Array },
			sizeGuide: { type: String },
			introTitle: { type: String },
			introSteps: { type: Array },
			fullscreen: { type: Boolean, reflect: true },
			step: { type: Number },
			showSessionStep: { type: Boolean },
			showOnboardingSteps: { type: Boolean },
		}
	}

	constructor() {
		super();

		this.pathTitle = '';
		this.pathDescription = '';
		this.styleTitle = '';
		this.styleDescription = '';
		this.fromScratchLabel = 'Start from scratch';
		this.fromScratchImage = '';
		this.comboLabel = 'Bundle with a combo';
		this.collectionHandle = null;

		this.sizeTitle = '';
		this.sizeDescription = '';
		this.sizeDisclaimer = '';

		this.fabricTitle = '';
		this.fabricDescription = '';
		this.fabrics = [];

		this.sessionTitle = '';
		this.sessionDescription = '';
		this.sessionImage = sessionStorage.getItem(`bundleImage${theme.product.id}`);

		this.products = [];
		this.sizes = 
			window.theme.storeLocation == 'US' ? ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cali King'] 
			: window.theme.storeLocation == 'UK' ? ['Single', 'Double', 'King', 'Super King']
			: ['Single', 'King Single', 'Double', 'Queen', 'King', 'Super King'];
		this.prevPanels = [];
		this.nextPanel = null;
		this.panel = 'path';

		this.introTitle = '';
		this.introSteps = [];

		this.product = null;
		this.size = null;
		this.fabric = null;
		this.step = 0;
		this.totalSteps = (this.showOnboardingSteps ? 4 : 2);

		this.fullscreen = true;
		this.clearOnClose = false;

		this.registerTouchEvents();

		const loadObserver = new IntersectionObserver(entries => {
			if (entries.some(e => e.isIntersecting)) {
				loadObserver.disconnect();
				this.load();
			}
		});

		loadObserver.observe(this);
	}

	async connectedCallback() {
		super.connectedCallback();

		if (!this.collectionHandle || this.collectionHandle.length == 0) this.hidden = true;
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('introSteps')) {
				this.totalSteps = this.introSteps.length + (this.showOnboardingSteps ? 4 : 2);
				this.panel = introSteps[0]?.id ?? 'path';
		}
		
		this.nextPanel = null;

		if (this.introSteps.some(step => step.id == this.panel)) {
			const stepIdx = this.introSteps.findIndex(step => step.id == this.panel);
			this.nextPanel = 
				this.introSteps[stepIdx + 1] ? this.introSteps[stepIdx + 1].id : 'path';
		}
	}

	async load() {
		const query = /* GraphQL */ `
			query collectionQuery($handle: String!) @inContext(country: ${theme.countryCode}) {
				collection(handle: $handle) {
					products(first: 50) {
						nodes {
							id
							handle
							featuredImage {
								url
							}
							options {
								name
								values
							}
							childItems: metafield(namespace: "bundle", key: "child_products") {
								references(first: 10) {
									nodes {
										... on Product {
											handle
											productType
											options {
												name
												values
											}
										}
									}
								}
							}
							colours: metafield(namespace: "custom", key: "colours") {
								value
							}
						}
					}
				}
			}
		`

		const resp = await fetch('/api/2025-01/graphql.json', {
			method: 'POST',
			headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': theme.storefrontToken
			},
			body: JSON.stringify({query: query, variables: {handle: this.collectionHandle} })
		});

		const json = await resp.json();

		const getConfig = (product) => {
			const config = product.childItems.references.nodes.map(node => ({
				Type: node.productType.replace('Scalloped', '').trim(),
				Colour: node.options.find(option => option.name == 'Colour').values[0],
			}));

			const colours = product.colours ? JSON.parse(product.colours.value) : [];
			config.sort((a, b) => colours.indexOf(a.Colour) - colours.indexOf(b.Colour));

			return config;
		}

		this.products = json.data.collection.products.nodes.map(product => ({
			handle: product.handle,
			image: product.featuredImage.url,
			colour: product.options.find(option => option.name == 'Colour').values[0],
			config: getConfig(product),
		}));
	}

	registerTouchEvents() {
		let touchStart = null;
		let swiping = false;

		let diffX = 0;
		let diffY = 0;
		let offset = 0;

		this.addEventListener('touchstart', e => {
			touchStart = e.touches[0];
		});

		this.addEventListener('touchmove', e => {
			if (!touchStart) return;

			const touch = e.touches[0];

			diffX = touch.clientX - touchStart.clientX;
			diffY = touch.clientY - touchStart.clientY;
			offset = 0;

			if (Math.abs(diffY) < 50 || swiping) {
				if (Math.abs(diffX) > 5) {
					offset = diffX;

					if (!this.nextPanel) offset = Math.max(0, offset);
					if (this.prevPanels.length == 0) offset = Math.min(0, offset);

					if (offset !== 0) {
						this.classList.remove('swipe-transition');
						this.style.setProperty('--swipe-offset', `${offset}px`);
						swiping = true;
					}
				}
			}
			else touchStart = null; // Cancel swipe if vertical movement is too large
		});

		this.addEventListener('touchend', e => {
			if (swiping) {
				this.style.setProperty('--swipe-anim-offset', `${offset}px`);

				if (offset > 50 && this.prevPanels.length > 0) this.setPanel(this.prevPanels.pop());
				else if (offset < -50 && this.nextPanel) this.setPanel(this.nextPanel);

				setTimeout(() => this.style.removeProperty('--swipe-anim-offset'), 400 * 0.8);
			}

			swiping = false;
			touchStart = null;

			this.classList.add('swipe-transition');
			setTimeout(() => this.style.removeProperty('--swipe-offset'));
		});
	}

	setPanel(panel) {
		this.shadowRoot.querySelectorAll('.panel').forEach(panel => panel.classList.remove('transition-in', 'transition-out', 'reverse'));
		this.shadowRoot.querySelector('.content').classList.remove('clip-animation');

		if (this.panelTransitionHandle) {
			clearTimeout(this.panelTransitionHandle);
			this.panelTransitionHandle = null;
		}

		const panels = [...this.shadowRoot.querySelectorAll('.panel')];
		const currentPanel = this.shadowRoot.querySelector(`.panel[data-panel="${this.panel}"]`);
		const nextPanel = this.shadowRoot.querySelector(`.panel[data-panel="${panel}"]`);

		if (currentPanel && nextPanel) {
			currentPanel.classList.add('transition-out');
			nextPanel.classList.add('transition-in');
			console.log(currentPanel.classList.value)

			const content = this.shadowRoot.querySelector('.content');
			content.style.setProperty('--clip-from', `inset(calc(50% - ${currentPanel.clientHeight / 2}px) calc(50% - ${currentPanel.clientWidth / 2}px))`);
			content.style.setProperty('--clip-to', `inset(calc(50% - ${nextPanel.clientHeight / 2}px) calc(50% - ${nextPanel.clientWidth / 2}px))`);
			content.classList.add('clip-animation');

			if (panels.indexOf(nextPanel) < panels.indexOf(currentPanel)) {
				currentPanel.classList.add('reverse');
				nextPanel.classList.add('reverse');
			}
			else {
				this.prevPanels.push(this.panel); 
			}

			this.panelTransitionHandle = setTimeout(() => {
				currentPanel.classList.remove('transition-out', 'reverse');
				nextPanel.classList.remove('transition-in', 'reverse');
				content.classList.remove('clip-animation');
			}, 600);
		}

		this.panel = panel;
		this.step = panels.indexOf(nextPanel);
		setTimeout(() => this.shadowRoot.querySelectorAll('.options').forEach(options => options.scrollTop = 0), 700);
	}

	open(hasSession = false, hasPreset = false) {
		if (this.style.display == 'flex') return;
		
		this.style.display = 'flex';
		this.classList.add('transition-in');
		document.body.classList.add('bundle-onboarding-open');

		if (hasPreset) {
			this.panel = 'size';
		}
		else if (!this.showOnboardingSteps) {
			this.panel = 'fabric';
		}

		const params = new URLSearchParams(window.location.search);
		if (params.has('fabric')) {
			const fabric = params.get('fabric');
			this.fabric = fabric.charAt(0).toUpperCase() + fabric.slice(1).toLowerCase();
			this.panel = 'size';
		}

		setTimeout(() => {
			this.classList.remove('transition-in');
		}, 300);
	}

	setClip() {
		const content = this.shadowRoot.querySelector('.content');

		const width = content.clientWidth
		const height = content.clientHeight;
		content.style.clipPath = `inset(calc(50% - ${height / 2}px) calc(50% - ${width / 2}px))`;
	}

	close(preset = false) {
		this.classList.add('transition-out');
		document.documentElement.classList.remove('modal-open');
		
		if (!preset) fireEvent('Start-Scratch');

		this.dispatchEvent(new CustomEvent('onboardingcomplete', { bubbles: true, detail: { config: this.product?.config, size: this.size, fabric: this.fabric } }));

		setTimeout(() => {
			trackEvent('BYOB Onboarding Closed', { size: this.size, preset: this.product?.colour });
			this.classList.remove('transition-out');
			this.style.display = 'none';
			this.panel = 'path';
			this.step = this.introSteps.length;
			this.product = null;
			this.size = null;
			this.fabric = null;
			this.prevPanels = this.introSteps.map(s => s.id); 
			document.documentElement.classList.remove('modal-open');
			document.body.classList.remove('bundle-onboarding-open');
		}, 350);
	}

	selectProduct(product) {
		this.product = product;
		trackEvent('BYOB Onboarding Select Preset', { colour: this.product.colour });

		if (this.sizes.length > 0) this.setPanel('size');
		else this.completeSelection();
	}

	selectSize(size) {
		this.size = size;
		trackEvent('BYOB Onboarding Select Size', { size: this.size });
		this.completeSelection();
	}

	selectFabric(fabric) {
		this.fabric = fabric;
		trackEvent('BYOB Onboarding Select Fabric', { fabric: this.fabric });
		this.setPanel('size');
	}

	completeSelection() {
		if (this.clearOnClose) {
			document.querySelector('bundle-builder').restart();
			this.prevPanels = this.introSteps.map(step => step.id);
		}

		this.close(true);

		if (this.product) {
			fireEvent('Start-Curated', this.product.colour.replace(/&amp;/g, '&').trim());
			trackEvent('BYOB Select Curated', { colour: this.product.colour });
		}
	}

	skipOnboarding() {
		trackEvent('BYOB Onboarding Skipped', { step: this.step + 1, stepId: this.panel });
		this.setPanel('path');
	}

	// Lit will render HTML as a string by default, so we need to convert it to a document fragment
	createFragment(string) {
		return document.createRange().createContextualFragment(string);
	}

	render() {
		const introSteps = this.introSteps;

		return html`
			<div class="bg" @click=${this.close}  data-fs-element="BYOB Onboarding Background"></div>
			<div class="content" panel=${this.panel}>
				<button aria-label="Close" name="close" @click=${this.close} data-fs-element="BYOB Onboarding Close Button">
					<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path fill-rule="evenodd" clip-rule="evenodd" d="M10.9402 12.6281L5.46974 18.0971L6.53024 19.1591L12.0007 13.6886L17.4697 19.1591L18.5317 18.0986L13.0612 12.6281L18.5317 7.15913L17.4697 6.09863L12.0007 11.5676L6.53174 6.09863L5.46974 7.15913L10.9402 12.6281Z" fill="#212121"/>
					</svg>				
				</button>
				${this.totalSteps > 2 ? html`<div class="progress" style="--step: ${this.step}; --steps: ${this.totalSteps}" style="display: none;"></div>` : ''}
				${this.prevPanels.length > 0 ? 
					html`<button aria-label="Back" name="back" @click=${() => this.setPanel(this.prevPanels.pop())} data-fs-element="BYOB Onboarding Back Button"></button>` : ''
				}
				${introSteps.map((step, idx) => html`
					<div class="panel step ${idx == 0 ? 'first-step': ''}" ?active="${this.panel == step.id}" data-panel="${step.id}">
						${this.introTitle && idx == 0 ? html`<h1>${this.introTitle}</h1>` : ''}
						${step.video != null ? this.renderVideo(step.video) : step.image != null ? html`<img src="${step.image}">` : ''}	
						<div class="header">
							<h2>${step.title}</h2>
							<div><p>${this.createFragment(step.description)}</p></div>
							<button class="button button--primary" @click=${() => this.setPanel(this.nextPanel)} data-fs-element="BYOB Onboarding Next Button">Next</button>
							<button class="button button--tertiary" @click=${() => this.skipOnboarding()} data-fs-element="BYOB Onboarding Skip Button" data-step="${idx + 1}" step="${idx + 1}">Skip and Start Bundling</button>
						</div>
					</div>
				`)}
				${this.nextPanel ? html`
					<button aria-label="Next" name="next" @click=${() => this.setPanel(this.nextPanel)} data-fs-element="BYOB Onboarding Next Button"></button>
				` : ''}
				<div class="panel path" ?active="${this.panel == 'path'}" data-panel="path">
					<div class="header">
						<h2>${this.pathTitle}</h2>
						<div>${this.createFragment(this.pathDescription)}</div>
					</div>
					<ul class="options">
						<button class="option" loading="${this.products.length == 0}" role="listitem" style="--delay: 50ms" @click=${() => this.setPanel('style')} data-fs-element="BYOB Explore Curated">
							${this.products.length > 0 ? html`<bundle-preset-image .config="${this.products[0].config}" fallback=${this.products[0].image} handle=${this.products[0].handle}></bundle-preset-image>` : html`<div></div>`}
							<label>${this.comboLabel}</label>
						</button>
						<button class="option" role="listitem" @click="${() => this.setPanel('fabric')}" data-fs-element="BYOB Start From Scratch">
							<bundle-preset-image fallback=${this.fromScratchImage} handle="default"></bundle-preset-image>
							<label>${this.fromScratchLabel}</label>
						</button>
					</ul>
				</div>

				<div class="panel style" ?active="${this.panel == 'style'}" data-panel="style">
					<div class="header">
						<h2>${this.styleTitle}</h2>
						<div>${this.createFragment(this.styleDescription)}</div>
					</div>
					<ul class="options">
						${this.products.map((product, idx) => html`
							<button class="option" role="listitem" style="--delay: ${idx * 50}ms" @click=${() => this.selectProduct(product)} data-fs-element="BYOB Curated Bundle">
								<bundle-preset-image .config="${product.config}" fallback=${product.image} handle=${product.handle}></bundle-preset-image>
								<label>${product.colour.replace(/&amp;/g, '&')}</label>
							</button>
						`)}
					</ul>
				</div>

				<div class="panel fabric-panel" ?active="${this.panel == 'fabric'}" data-panel="fabric">
					<div class="header">
						<h2>${this.fabricTitle}</h2>
						<div>${this.createFragment(this.fabricDescription)}</div>
					</div>
					<ul class="fabrics">
						${this.fabrics.map(fabric => html`
							<div class="fabric">
								<img src="${fabric.image}" alt="${fabric.title}" loading="lazy" class="fabric__image">
								<div class="fabric__details">
									<h3 class="fabric__title">${fabric.title}</h3>
									<p class="fabric__description">${this.createFragment(fabric.description)}</p>
									<button class="fabric__button button button--secondary" @click=${() => this.selectFabric(fabric.name)}>
										${this.createFragment(fabric.buttonLabel.replaceAll('✨', '<span class="sparkle">✨</span>'))}
									</button>
								</div>
							</div>
						`)}
					</ul>
				</div>

				<div class="panel size" ?active="${this.panel == 'size'}" data-panel="size">
					<div class="header">
						<h2>${this.sizeTitle}</h2>
						<div>${this.createFragment(this.sizeDescription)}</div>
					</div>
					<ul class="sizes">
						${this.sizes.map((size, idx) => html`
							<button class="size-button button button--secondary" role="listitem" style="--delay: ${idx * 50}ms" @click=${() => this.selectSize(size)} data-fs-element="BYOB Onboarding Size Button">
								${size}
							</button>
						`)}
					</ul>
					<button class="size-guide" @click=${() => this.dispatchEvent(new CustomEvent('opensizeguide', { detail: { handle: this.sizeGuide }, bubbles: true, composed: true }))}>Size guide</button>
					<p class="disclaimer">${this.sizeDisclaimer}</p>
				</div>
			</div>
		`
	}

	clearSession() {
		this.clearOnClose = true;
		this.setPanel('path');
	}

	renderVideo(video) {
		let source = null;

		for (const s of video.sources) {
			if (s.format !== 'mp4') continue;
			if (source == null || s.width > source.width) source = s;
		}
		
		return html`
			<video preload="metadata" autoplay muted loop playsinline>
				<source src="${source.url}" type="video/mp4">
			</video>
		`
	}

	static get styles() {
		return css`
			${buttonStyles}

			:host {
				display: none;
				position: fixed;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				z-index: 2000;
				justify-content: center;
				align-items: end;
				transition: opacity 200ms;
				--panel-transition-speed: 0.8;
				--swipe-offset: 0px;
			}

			:host([hidden]) {
				display: none !important;
			}

			:host(.transition-in) {
				animation: fade-in 300ms backwards;
			}

			:host(.transition-out) {
				opacity: 0;
			}

			.bg {
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				background: var(--overlay, rgba(0, 0, 0, 0.20));
			}

			.content {
				background-color: white;
				border-radius: var(--xxs, 4px);
				border-bottom-left-radius: 0;
				border-bottom-right-radius: 0;
				box-shadow: 0px 8px 12px 0px rgba(0, 0, 0, 0.12); 
				text-align: center;
				max-height: min(90%, 500px);
				transition: transform 200ms, box-shadow 300ms;
				overflow: hidden;
				position: relative;
				justify-items: center;
				align-items: start;
				display: grid;
				width: 100%;
			}

			:host(.transition-in) .content {
				animation: content-in 300ms backwards;
			}

			:host(.transition-out) .content {
				transform: translateY(10px);
			}

			@media screen and (min-width: 992px) {
				@keyframes clip-transition {
					from {
						clip-path: var(--clip-from);
					}
					to {
						clip-path: var(--clip-to);
					}
				}

				:host {
					align-items: center;
				}

				.content {
					width: auto;
					align-items: center;
					max-height: min(90%, 800px);
					border-radius: var(--xxs, 4px);
				}

				.content[panel="fabric"] {
					overflow: visible;
				}
	
				.content.clip-animation {
					animation: clip-transition calc(550ms / var(--panel-transition-speed)) both;
					box-shadow: 0px 8px 12px 0px rgba(0, 0, 0, 0); 
				}
	
				.content.clip-animation button[name="close"] {
					opacity: 0;
				}
			}

			button[name="back"],
			button[name="next"] {
				width: 40px;
				height: 40px;
				padding: 0;
				border: none;
				background: none;
				background-image: var(--icon-arrow-right);
				background-size: 100%;
				transform: scaleX(-1) translateY(-50%);
				position: absolute;
				top: 50%;
				z-index: 50;
				transition: top 150ms;
			}

			button[name="back"] {
				left: 0;
			}

			button[name="next"] {
				right: 0;
				transform: translateY(-50%);
			}

			button[name="close"] {
				background: none;
				border: none;
				padding: 0;
				display: block;
				position: absolute;
				top: 12px;
				right: 12px;
				width: 32px;
				height: 32px;
				padding: 4px;
				cursor: pointer;
				border-radius: 4px;
				z-index: 200;
				transition: opacity 250ms;
			}

			@media (hover: hover) {
				button[name="close"]:hover,
				button[name="back"]:hover,
				button[name="next"]:hover {
					background-color: var(--button-bg-hover);
				}
			}

			button[name="close"]:active,
			button[name="back"]:active,
			button[name="next"]:active {
				background-color: var(--button-bg-active);
			}

			.panel {
				padding: var(--distance-m, 24px) var(--distance-m, 24px) var(--distance-xl, 40px) var(--distance-m, 24px); 
				display: none;
				flex-direction: column;
				overflow: hidden;
				position: relative;
				grid-column: 1;
				grid-row: 1;
				background: white;
				box-sizing: border-box;
				align-items: center;
				justify-content: center;
				height: 500px;
				transform: translateX(var(--swipe-offset, 0px));
			}

			:host(.swipe-transition) .panel {
				transition: transform 250ms;
			}
		
			.panel[active] {
				display: flex;
			}

			.step {
				gap: var(--padding-sm);
			}

			.step h1 {
				font: var(--title-sm);
				margin-top: 0;
				margin-bottom: 0;
				color: var(--color-text-primary);
				display: none;
			}

			.step:has(h1) h1 {
				display: block;
			}

			@keyframes image-in {
				from {
					opacity: 0;
				}
				to {
					opacity: 1;
				}
			}

			.step :is(img, video, .image-placeholder) {
				display: block;
				width: 100%;
				height: auto;
				aspect-ratio: 4 / 5;
				object-fit: cover;
				background-color: var(--color-bg-secondary, #F5F5F5);
				max-width: 260px;
			}

			.step :is(img, video) {
				animation: image-in 200ms both;
			}

			.step .button {
				margin-top: var(--padding-xs);
			}

			.step .button--primary {
				min-width: 120px;
			}

			.step.session .button {
				min-width: 232px;
				min-height: 40px;
			}

			@media screen and (max-width: 512px) {
				.step:has(h1) :is(img, video, .image-placeholder) {
					max-width: 230px;
				}
			}

			@media screen and (min-width: 992px) {
				.step {
					grid-template-columns: 1fr 1fr;
					column-gap: var(--padding-lg);
					align-content: center;
				}

				.step :is(img, video, .image-placeholder) {
					max-width: unset;
				}

				.step h1 {
					font: var(--title-lg);
					margin-bottom: var(--padding-sm);
				}

				.step:has(h1) :is(img, video, .image-placeholder) {
					grid-column: 1;
					grid-row: 1 / span 2;
				}

				.step:has(h1) h1 {
					align-self: end;
					text-align: left;
				}

				.step:has(h1) .header {
					align-self: start;
				}

				.step[active] {
					display: grid;
				}

				.step .header {
					align-self: center;
					align-items: start;
					text-align: left;
				}
			}

			.style::after {
				content: '';
				display: block;
				position: absolute;
				left: 0;
				bottom: 0;
				right: 0;
				height: 40px;
				background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%);
				z-index: 1;
			}

			@media screen and (min-width: 992px) {
				.panel {
					height: 400px;
					min-width: 632px;
				}

				.panel.style {
					height: 80vh;
				}
			}

			h2 {
				margin: 0;
				font: var(--title-sm);
				color: var(--color-text-primary);
			}

			p {
				margin: 0;
				font-size: 14px;
				font-weight: 400;
				position: relative;
				z-index: 100;
			}

			p:not(:last-child) {
				margin-bottom: 4px;
			}

			strong {
				font-weight: 500;
			}

			a {
				color: var(--color-text-primary);
				text-decoration: underline;
			}

			.header {
				position: relative;
				z-index: 100;
				align-self: stretch;
				text-align: center;
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: var(--padding-xs);
			}

			.style .header::after {
				content: '';
				display: block;
				position: absolute;
				left: 0;
				top: 100%;
				right: 0;
				height: 24px;
				background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%);
			}

			.header > div {
				max-width: 472px;
			}

			.size-guide {
				font: var(--text-md);
				border: none;
				background: none;
				padding: 0;
				padding-block: 8px;
				color: var(--color-text-tertiary, #575757);
				text-decoration: underline;
				cursor: pointer;
			}

			.disclaimer {
				margin-top: var(--padding-sm);
			}

			.disclaimer:empty {
				display: none;
			}

			@keyframes fade-in {
				from { 
					opacity: 0; 
				}
				to { 
					opacity: 1; 
				}
			}

			@keyframes fly-in {
				from { 
					opacity: 0; 
					transform: translateY(5px);
				}
				to { 
					opacity: 1; 
					transform: translateY(0);
				}
			}

			@keyframes content-in {
				from { 
					transform: translateY(10px);
				}
				to { 
					transform: translateY(0);
				}
			}

			.style {
				flex: 1;
				width: min(calc(100vw - 32px), 832px);
				justify-self: center;
			}

			:is(.style, .size) h2::after {
				content: '';
				display: block;
				width: 24px;
			}

			.size .button {
				text-align: left;
			}

			@keyframes slide-in {
				from {
					transform: translateX(150%);
					opacity: 0;
				}
				to {
					transform: translateX(0);
					opacity: 1;
				}
			}

			@keyframes slide-out {
				from {
					transform: translateX(var(--swipe-anim-offset, 0px));
					opacity: 1;
				}
				to {
					transform: translateX(-150%);
					opacity: 0;
				}
			}

			@keyframes slide-in-reverse {
				from {
					transform: translateX(-150%);
					opacity: 0;
				}
				to {
					transform: translateX(0);
					opacity: 1;
				}
			}

			@keyframes slide-out-reverse {
				from {
					transform: translateX(var(--swipe-anim-offset, 0px));
					opacity: 1;
				}
				to {
					transform: translateX(150%);
					opacity: 0;
				}
			}

			.panel:is(.transition-in, .transition-out) {
				display: flex;
			}

			.step.transition-in, .step.transition-out {
				display: flex;
			}

			.panel.transition-in {
				animation: slide-in calc(400ms / var(--panel-transition-speed)) both;
			}

			.panel.transition-in.reverse {
				animation: slide-in-reverse calc(400ms / var(--panel-transition-speed)) both;
			}

			.panel.transition-out {
				animation: slide-out calc(400ms / var(--panel-transition-speed)) both;
			}

			.panel.transition-out.reverse {
				animation: slide-out-reverse calc(400ms / var(--panel-transition-speed)) both;
			}

			.options {
				display: grid;
				margin: 0;
				padding: 0;
				overflow-y: auto;
				overflow-x: hidden;
				gap: var(--padding-sm);
				padding-block: var(--padding-sm);
				padding-bottom: 40px;
				margin-bottom: -40px;
				grid-template-columns: 1fr 1fr;
				align-self: stretch;
				justify-content: center;
			}

			.style .options {
				grid-template-columns: repeat(2, 1fr);
			}

			.sizes {
				display: grid;
				gap: var(--padding-xs);
				grid-template-columns: 150px;
				margin: 0;
				padding: 0;
				padding-block: var(--padding-sm);
			}

			.size-button {
				font: var(--text-md);
			}

			@media screen and (min-width: 768px) {
				.options { 
					grid-template-columns: repeat(2, 160px);
				}

				.style .options {
					grid-template-columns: repeat(3, minmax(160px, 1fr));
				}

				.sizes {
					grid-template-columns: repeat(2, 200px);
				}
			}

			@media screen and (min-width: 992px) {
				.step.transition-in, .step.transition-out {
					display: grid;
				}

				.options { 
					grid-template-columns: repeat(2, 210px);
				}

				.style .options {
					grid-template-columns: repeat(4, minmax(180px, 1fr));
				}
			}

			.option {
				border-radius: var(--xxs, 4px);
				border: 1px solid var(--border-border-primary, #E1E1E1);
				background: var(--white-white, #FFF); 
				box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.12); 
				font: var(--text-md);
				font-family: inherit;
				display: flex;
				flex-direction: column;
				justify-content: end;
				align-items: center;
				cursor: pointer;
				transition: transform 250ms, box-shadow 250ms, background 150ms, opacity 300ms;
				position: relative;
				z-index: 1;
				padding: 0;
			}

			bundle-preset-image, .option div {
				max-width: 100%;
				width: 100%;
				aspect-ratio: 1.2;
				display: block;
				flex: 1;
				transition: filter 150ms;
			}

			.option[loading="true"] {
				pointer-events: none;
				opacity: 40%;
			}

			.option[loading="true"]::after {
				content: '';
				position: absolute;
				height: 24px;
				width: 24px;
				left: calc(50% - 12px);
				top: calc(50% - 24px);
				opacity: 60%;
				animation: show-loading-anim 1.5s linear infinite;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z'/%3E%3C/svg%3E");
			}

			@keyframes show-loading-anim {
				0% {
						transform: rotate(0deg);
				}
				100% {
						transform: rotate(360deg);
				}
			}

			@media (hover: hover) {
				.option:hover {
					background: rgba(0, 0, 0, 0.05);
					transition: transform 150ms, box-shadow 150ms;
					transform: translateY(-3px);
					box-shadow: 0px 6px 12px 0px rgba(0, 0, 0, 0.14);  
				}

				.option:hover bundle-preset-image {
					filter: brightness(0.96);
					transition: none;
				}
			}

			.option:active {
				background: rgba(0, 0, 0, 0.08);
				transition: none;
				transform: translateY(-1px);
				box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.10); 
			}

			.option:active bundle-preset-image {
				filter: brightness(0.92);
				transition: none;
			}

			.option label {
				padding: 4px;
				cursor: pointer;
				text-align: center;
				color: var(--color-text-primary);
			}

			.fabrics {
				display: flex;
				flex-direction: column;
				gap: var(--padding-sm);
				padding: 0;
				width: 100%;
				min-height: 0;
			}

			.fabric {
				display: flex;
				text-align: left;
				position: relative;
			}

			.fabric__image {
				width: 120px;
				height: auto;
				aspect-ratio: 4/5;
				object-fit: cover;
			}

			.fabric__details {
				display: flex;
				flex-direction: column;
				gap: var(--padding-xs);
				padding-block: var(--padding-xs);
				padding-inline: var(--padding-sm);
				padding-right: 0;
			}

			.fabric__details a {
				white-space: nowrap;
			}

			.fabric__title {
				margin: 0;
				color: var(--color-text-primary);
				font: var(--text-md);
			}

			.fabric__description {
				margin: 0;
				color: var(--color-text-tertiary);
				font: var(--text-md);
			}

			.fabric__description ul {
				padding-left: var(--padding-sm);
  			list-style: disc;
			}

			.fabric__button {
				margin-top: auto;
				padding-block: 4px;
				font: var(--text-md);
				align-self: start;
				gap: 4px;
			}

			.fabric__button::after {
				position: absolute;
				content: '';
				display: block;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
			}

			@media screen and (min-width: 768px) {
				.fabric-panel {
					padding: 0;
					overflow: visible;
				}

				.fabrics {
					flex-direction: row;
					justify-content: center;
					margin-bottom: 0;
				}

				.fabric {
					flex-direction: column;
					max-width: min(290px, calc((100vh - var(--header-height) - 200px) * 0.6))
				}

				.fabric__details {
					padding-block: var(--padding-sm);
					padding-inline: var(--padding-xs);
					flex: 1;
				}

				@media (max-height: 680px) {
					.fabrics {
						margin-top: var(--padding-xs);
					}

					.fabric__details {
						padding-inline: 0;
					}
				}

				.fabric__image {
					width: 100%;
				}

				.fabric__description {
					margin-bottom: var(--padding-sm);
				}

				.fabric__title, .fabric__button {
					align-self: center;
				}
			}

			:host([fullscreen]) {
				transition: none;
				align-items: center;
				justify-content: center;
				z-index: 500;
			}

			:host([fullscreen]) .bg {
				pointer-events: none;
				background: white;
				z-index: 0;
			}

			:host([fullscreen]) .content {
				animation: none !important;
				transition: none !important;
				box-shadow: none !important;
				width: min(100vw, 932px);
				align-items: center;
				justify-content: stretch;
				justify-items: stretch;
				height: 100%;
				max-height: 90%;
			}

			:host([fullscreen]) .panel {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
			}

			:host([fullscreen]) .panel.style {
				height: calc(90vh - var(--header-height));
			}

			@media screen and (max-width: 992px) {
				:host([fullscreen]) .content {
					max-height: unset;
					height: auto;
					position: absolute;
					top: var(--header-height);
					left: 0;
					right: 0;
					bottom: 0;
				}

				:host([fullscreen]) .panel.style {
					height: calc(95vh - var(--header-height));
				}
			}

			:host([fullscreen]) button[name="close"] {
				display: none;
			}

			.progress {
				position: absolute;
				top: 8px;
				left: 24px;
				right: 24px;
				height: 2px;
				border-radius: 50px;
				background: var(--color-border-border-primary);
				z-index: 5;
			}

			.progress::before {
				content: '';
				display: block;
				height: 2px;
				width: calc(100% / var(--steps));
				background: var(--color-bg-inverted-secondary, #434343);
				border-radius: 50px;
				transform: translateX(calc(100% * var(--step)));
				transition: transform 500ms;
			}

			@media screen and (min-width: 992px) {
				:host([fullscreen]) {
					top: var(--header-height);
				}

				:host([fullscreen]) .panel {
					padding-inline: 44px;
				}

				.progress {
					top: 0;
					left: 0;
					right: 0;
				}
			}
		`;
	}
}

customElements.define('bundle-onboarding', BundleOnboarding);


class BundlePresetImage extends LitElement {
	static get properties() {
		return {
			config: { type: Object },
			image: { type: String },
			fallback: { type: String },
			handle: { type: String }
		}
	}

	constructor() {
		super();

		this.handle = null;
		this.config = null;
		this.image = null;
		this.fallback = null;

		const params = new URLSearchParams(window.location.search);
		if (params.get('regenerate_presets')) this.regenerate = true;
		else this.regenerate = false;
	}

	async getImage() {
		const renderer = document.querySelector('byob-render');

		if (renderer) {
			const renderConfig = this.config ? {
				variants: this.config.map(item => ({type: item.Type, colour: item.Colour}))
			} : null;

			// Wait for renderer to be ready
			await new Promise(resolve => {
				const check = () => {
					if (renderer.ready) {
						resolve();
						return;
					}
					setTimeout(check, 500);
				}
				check();
			});

			this.image = renderer.createScreenshot(300, 300, { config: renderConfig, enableDecorations: false });

			if (this.handle != null && this.regenerate) {
				await fetch(`https://bundle-images.bedthreads.workers.dev/preset/${this.handle}`, { method: 'POST', body: JSON.stringify({ image: this.image}) });
			}
		}
		else this.image = this.fallback;
	}

	render() {
		return html`<img loading="lazy" src="${this.image || this.regenerate ? this.image : `https://bundle-presets.bedthreads.com/${this.handle}.jpg` }" 
			@load=${() => this.classList.add('loaded')}
			@error=${() => this.getImage()}
		/>`
	}

	static get styles() {
		return css`
			:host {
				display: block;
				position: relative;
				background: rgba(0, 0, 0, 0.02);
			}

			:host::before {
				content: '';
				position: absolute;
				height: 24px;
				width: 24px;
				left: calc(50% - 12px);
				top: calc(50% - 12px);
				opacity: 20%;
				animation: show-loading-anim 1.5s linear infinite;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z'/%3E%3C/svg%3E");
			}

			:host(:not(.loaded)) img {
				visibility: hidden;
			}

			:host(.loaded)::before {
				display: none;
			}


			@keyframes fade-in {
				from {
					opacity: 0;
				}
				to {
					opacity: 1;
				}
			}

			img {
				object-fit: cover;
				width: 100%;
				height: 100%;
				max-height: 100%;
				position: relative;
				z-index: 1;
				animation: fade-in 300ms;
				display: block;
			}
		`
	}
}

customElements.define('bundle-preset-image', BundlePresetImage);
